<template>
    <div>
        <!-- Modals -->
        <!-- NOTE - Add new Account -->
        <b-modal
            size="lg"
            ref="add-account-modal"
            id="add-account-modal"
            centered
            title="Add new Account"
        >
            <!-- Preloader -->
            <PagePreloader v-if="modals.facebook.isLoading"></PagePreloader>

            <!-- Error management -->
            <CustomAlerts :errors="modals.facebook.errors"></CustomAlerts>

            <p>
                To add an account to the Hashtastic Insights Tracker, you will need to link your
                Instagram Account with Hashtastic via Facebook. Click the button below to be
                redirected to Facebook.
            </p>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <b-button
                    type="submit"
                    variant="success"
                    @click="addAccount"
                    :disabled="modals.facebook.isLoading"
                >
                    Add Account
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>
        <!-- End add account modal -->

        <!-- NOTE - Add new Campaign -->
        <b-modal
            size="lg"
            ref="add-campaign-modal"
            id="add-campaign-modal"
            centered
            title="Add new Campaign"
        >
            <!-- Preloader -->
            <PagePreloader v-if="modals.campaign.isLoading"></PagePreloader>

            <!-- Error management -->
            <CustomAlerts :errors="modals.campaign.errors"></CustomAlerts>

            <!-- Check campaign count -->
            <template v-if="dashboard.globals">
                <div class="card card-custom card-body gutter-b">
                    <div class="d-flex align-items-center mr-2">
                        <!--begin::Symbol-->
                        <div class="symbol symbol-45 symbol-light-success mr-4 flex-shrink-0">
                            <div class="symbol-label">
                                <span class="svg-icon svg-icon-lg svg-icon-success">
                                    <inline-svg
                                        src="/media/svg/icons/Shopping/Barcode-read.svg"
                                    ></inline-svg>
                                </span>
                            </div>
                        </div>
                        <!--end::Symbol-->
                        <!--begin::Title-->
                        <div>
                            <div class="font-size-h4 text-dark-75 font-weight-bold">
                                You are currently running {{ dashboard.globals.campaigns_current }}
                                /
                                {{ dashboard.globals.campaigns_max }} Campaigns.
                            </div>
                        </div>
                        <!--end::Title-->
                    </div>
                </div>
            </template>
            <template v-if="currentAccount.user">
                <template v-if="dashboard.globals">
                    <template v-if="canAddCampaigns">
                        <div class="row col-xl-12">
                            <div class="card card-custom card-body gutter-b">
                                <div class="pt-lg-0 pt-5 text-center text-md-left">
                                    <div class="font-weight-bold font-size-h4">
                                        Is this the account you would like to start a Hashtag
                                        Performance Campaign on?
                                    </div>
                                </div>
                            </div>
                            <div class="row col-xl-12">
                                <template v-if="currentAccount.user">
                                    <AccountBadgeWide :account="currentAccount.user">
                                    </AccountBadgeWide>
                                </template>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div
                            class="alert alert-custom alert-notice alert-light-warning fade show mb-5"
                            role="alert"
                        >
                            <div class="alert-icon">
                                <i class="flaticon-warning"></i>
                            </div>
                            <div class="alert-text">
                                Unfortunately you have run out of active Campaigns you can run. You
                                can either stop one of your current campaigns, or
                                <router-link to="/subscribe">upgrade your account</router-link> to
                                enable more concurrent Hashtag Performance Campaigns!
                            </div>
                            <div class="alert-close">
                                <button
                                    type="button"
                                    class="close"
                                    data-dismiss="alert"
                                    aria-label="Close"
                                >
                                    <span aria-hidden="true">
                                        <i class="ki ki-close"></i>
                                    </span>
                                </button>
                            </div>
                        </div>
                    </template>
                </template>
            </template>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <b-button
                    type="submit"
                    variant="success"
                    @click="addCampaign"
                    :disabled="modals.campaign.isLoading"
                >
                    Start Campaign
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>
        <!-- End add account modal -->

        <!-- Remove Campaign Modal -->
        <b-modal
            size="lg"
            ref="delete-campaign-modal"
            id="delete-campaign-modal"
            centered
            title="Are you sure you want to stop this Campaign?"
        >
            <!-- Error management -->
            <CustomAlerts :errors="modals.deletecampaign.errors"></CustomAlerts>

            <template v-if="currentAccount.user.globals">
                <div v-if="!currentAccount.user.globals.campaign_can_be_deleted">
                    <InfoBox
                        text="In order to avoid abusing the system Campaigns have to run for a minimum of 5 days before you can delete them. Try again later."
                    ></InfoBox>
                </div>
                <div v-else>
                    Your Campaign will be halted for the following user:
                    <code> @{{ currentAccount.user.username }}</code
                    >.
                </div>
            </template>

            <template v-slot:modal-footer="{ ok, cancel, hide }" v-if="currentAccount.user.globals">
                <b-button
                    type="submit"
                    variant="danger"
                    :disabled="modals.deletecampaign.isLoading"
                    @click="stopCampaign"
                    v-if="currentAccount.user.globals.campaign_can_be_deleted"
                >
                    Stop Campaign
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>

        <!-- Remove Account Modal -->
        <b-modal
            size="lg"
            ref="delete-account-modal"
            id="delete-account-modal"
            centered
            title="Are you sure you want to stop monitoring this Account?"
        >
            <!-- Error management -->
            <CustomAlerts :errors="modals.deleteaccount.errors"></CustomAlerts>

            <template v-if="currentAccount.user.globals">
                Monitoring will be halted for the following user:
                <code> @{{ currentAccount.user.username }}</code
                >.
            </template>

            <template v-slot:modal-footer="{ ok, cancel, hide }" v-if="currentAccount.user.globals">
                <b-button
                    type="submit"
                    variant="danger"
                    :disabled="modals.deleteaccount.isLoading"
                    @click="stopAccountMonitoring"
                >
                    Stop Monitoring
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>
        <!-- NOTE - Select Account -->

        <!-- <b-modal
            size="lg"
            ref="select-account-modal"
            id="select-account-modal"
            centered
            title="Select account"
            body-class="position-static"
        >
            <b-overlay :show="modals.selectAccount.isLoading" rounded="true">
                <CustomAlerts :errors="modals.selectAccount.errors"></CustomAlerts>

                <template v-if="dashboard.accounts">
                    <b-button-toolbar>
                        <b-button-group class="mx-auto">
                            <b-button
                                :variant="buttonVariant('all')"
                                @click="setSelectAccountType('all')"
                                >All</b-button
                            >
                            <b-button
                                :variant="buttonVariant('campaign')"
                                @click="setSelectAccountType('campaign')"
                                >Campaigns</b-button
                            >
                            <b-button
                                :variant="buttonVariant('scanner')"
                                @click="setSelectAccountType('scanner')"
                                >Tracked accounts</b-button
                            >
                        </b-button-group>
                    </b-button-toolbar>
                    <template v-if="modals.selectAccount.type == 'scanner'">
                        <b-button-toolbar class="gutter-t">
                            <b-button-group class="mx-auto">
                                <b-button
                                    :variant="buttonSubVariant('all')"
                                    @click="setSelectAccountSubType('all')"
                                    >All</b-button
                                >
                                <b-button
                                    :variant="buttonSubVariant('personal')"
                                    @click="setSelectAccountSubType('personal')"
                                    >Personal</b-button
                                >
                                <b-button
                                    :variant="buttonSubVariant('competitor')"
                                    @click="setSelectAccountSubType('competitor')"
                                    >Competitor</b-button
                                >
                            </b-button-group>
                        </b-button-toolbar>
                    </template>

                    <div class="table-responsive">
                        <table
                            class="table table-head-custom table-vertical-center"
                            id="kt_advance_table_widget_1"
                        >
                            <thead>
                                <tr class="text-left">
                                    <th class="pr-0" style="width: 50px">Account</th>
                                    <th style="min-width: 200px"></th>
                                    <th style="min-width: 150px">Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                <template v-for="account in dashboard.accounts">
                                    <template
                                        v-if="
                                            modalSelectAccountDisplay(account.type, account.subtype)
                                        "
                                    >
                                        <tr
                                            :key="account.accountid"
                                            class="bg-hover-light"
                                            @click="loadAccount(account)"
                                        >
                                            <td class="pr-0">
                                                <div class="symbol symbol-50 symbol-light mt-1">
                                                    <span class="symbol symbol-label">
                                                        <img
                                                            :src="account.data.profilepic"
                                                            class="
                                                                h-100
                                                                align-self-end
                                                                hover-pointer
                                                            "
                                                        />
                                                    </span>
                                                </div>
                                            </td>
                                            <td class="pl-0">
                                                <a
                                                    href="#"
                                                    class="
                                                        text-dark-75
                                                        font-weight-bolder
                                                        text-hover-primary
                                                        mb-1
                                                        font-size-lg
                                                        hover-pointer
                                                    "
                                                    >@{{ account.data.username }}</a
                                                >
                                                <span
                                                    class="
                                                        text-muted
                                                        font-weight-bold
                                                        text-muted
                                                        d-block
                                                    "
                                                    >{{
                                                        abbreviateNumber(account.data.followers)
                                                    }}
                                                    followers</span
                                                >
                                            </td>
                                            <td>
                                                <span
                                                    class="
                                                        text-dark-75
                                                        font-weight-bolder
                                                        d-block
                                                        font-size-lg
                                                    "
                                                >
                                                    <template v-if="account.type == 'campaign'">
                                                        <span
                                                            class="
                                                                label label-xl
                                                                font-weight-boldest
                                                                label-inline label-success
                                                            "
                                                            >Campaign</span
                                                        >
                                                    </template>
                                                    <template v-else>
                                                        <span
                                                            class="
                                                                label label-xl
                                                                font-weight-boldest
                                                                label-inline label-primary
                                                            "
                                                            >Tracked Account</span
                                                        >
                                                    </template>
                                                </span>
                                                <template v-if="account.subtype">
                                                    <span class="text-muted font-weight-bold">
                                                        <span
                                                            class="
                                                                label
                                                                label-sm
                                                                label-light
                                                                label-pill
                                                                label-inline
                                                                mr-2
                                                                text-capitalize
                                                            "
                                                        >
                                                            {{ account.subtype }}
                                                        </span>
                                                    </span>
                                                </template>
                                            </td>
                                        </tr>
                                    </template>
                                </template>
                            </tbody>
                        </table>
                    </div>
                </template>

                <template v-slot:modal-footer="{ ok, cancel, hide }">
                    <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
                </template>
            </b-overlay>
        </b-modal> -->

        <!-- End add account modal -->

        <!-- <b-button class="btn btn-primary btn-lg" @click="openSelectAccountModal"
            >Select account</b-button
        > -->
        <!-- <b-button class="btn btn-primary btn-lg gutter-b" @click="openWizardSection()"
            >Add new accounts</b-button
        > -->

        <!-- SECTION - Main section -->
        <template v-if="currentSection == 'starter'">
            <div class="row">
                <div class="col-xl-12">
                    <template v-if="dashboardIsLoading">
                        <PagePreloader>accounts</PagePreloader>
                    </template>
                    <template v-else>
                        <template v-if="!dashboard.accounts">
                            <PagePreloader>accounts</PagePreloader>
                        </template>
                        <template v-if="dashboard.accounts">
                            <!--begin::Nav Panel Widget 1-->
                            <div class="card card-custom gutter-b">
                                <div class="card-header">
                                    <div class="card-title">
                                        <span class="card-icon">
                                            <i class="fas fa-hashtag text-success"></i>
                                        </span>
                                        <div class="card-label">Account Dashboard</div>
                                    </div>
                                    <div class="card-toolbar">
                                        <b-input-group class="mb-2 mr-sm-2 mb-sm-0">
                                            <b-input-group-prepend is-text>
                                                <b-icon icon="search"></b-icon>
                                            </b-input-group-prepend>
                                            <b-form-input
                                                placeholder="Search for an account here"
                                                v-model="searchAccount"
                                            ></b-form-input>
                                        </b-input-group>
                                    </div>
                                </div>
                                <!--begin::Body-->
                                <div class="card-body">
                                    <p>
                                        Welcome to your
                                        <span class="text-success font-weight-bolder"
                                            >Account Dashboard</span
                                        >, this module enables you to track and monitor all your
                                        Instagram accounts with a single Dashboard.
                                    </p>
                                    <p>
                                        <span class="text-warning font-weight-bolder">
                                            Please note:
                                        </span>
                                        This new tool is still in Beta and is currently being worked
                                        on and improved. If you spot anything that doesn't work or
                                        looks out of the ordinary, feel free to drop us a message
                                        via
                                        <router-link to="/support" class="font-weight-bolder"
                                            >the Support page</router-link
                                        >
                                        so that we can look into it ASAP!
                                    </p>

                                    <b-button
                                        class="gutter-b"
                                        @click="openWizardSection()"
                                        variant="success"
                                        >Add Instagram Account</b-button
                                    >
                                    <div class="card card-custom card-stretch">
                                        <div class="card-body p-0 d-flex flex-column">
                                            <!--begin::Items-->
                                            <div class="flex-grow-1 card-spacer">
                                                <div class="row row-paddingless">
                                                    <!--begin::Item-->
                                                    <div class="col-md-4 pb-5">
                                                        <div class="d-flex align-items-center mr-2">
                                                            <!--begin::Symbol-->
                                                            <div
                                                                class="symbol symbol-45 symbol-light-success mr-4 flex-shrink-0"
                                                            >
                                                                <div class="symbol-label">
                                                                    <span
                                                                        class="svg-icon svg-icon-lg svg-icon-success"
                                                                    >
                                                                        <inline-svg
                                                                            src="/media/svg/icons/Shopping/Barcode-read.svg"
                                                                        ></inline-svg>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <!--end::Symbol-->
                                                            <!--begin::Title-->
                                                            <div>
                                                                <div
                                                                    class="font-size-h4 text-dark-75 font-weight-bolder"
                                                                >
                                                                    {{
                                                                        dashboard.globals
                                                                            .campaigns_current
                                                                    }}
                                                                    /
                                                                    {{
                                                                        dashboard.globals
                                                                            .campaigns_max
                                                                    }}
                                                                </div>
                                                                <div
                                                                    class="font-size-sm text-muted font-weight-bold mt-1"
                                                                >
                                                                    Hashtag Performance Campaigns
                                                                </div>
                                                            </div>
                                                            <!--end::Title-->
                                                        </div>
                                                    </div>
                                                    <!--end::Item-->
                                                    <!--begin::Item-->
                                                    <div class="col-md-4 pb-5">
                                                        <div class="d-flex align-items-center mr-2">
                                                            <!--begin::Symbol-->
                                                            <div
                                                                class="symbol symbol-45 symbol-light-primary mr-4 flex-shrink-0"
                                                            >
                                                                <div class="symbol-label">
                                                                    <span
                                                                        class="svg-icon svg-icon-lg svg-icon-primary"
                                                                    >
                                                                        <inline-svg
                                                                            src="/media/svg/icons/Communication/Group.svg"
                                                                        ></inline-svg>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <!--end::Symbol-->
                                                            <!--begin::Title-->
                                                            <div>
                                                                <div
                                                                    class="font-size-h4 text-dark-75 font-weight-bolder"
                                                                >
                                                                    {{
                                                                        dashboard.globals
                                                                            .personal_accounts
                                                                    }}
                                                                </div>
                                                                <div
                                                                    class="font-size-sm text-muted font-weight-bold mt-1"
                                                                >
                                                                    Personal Accounts Monitored
                                                                </div>
                                                            </div>
                                                            <!--end::Title-->
                                                        </div>
                                                    </div>
                                                    <!--end::Item-->
                                                    <!--begin::Item-->
                                                    <div class="col-md-4 pb-5">
                                                        <div class="d-flex align-items-center mr-2">
                                                            <!--begin::Symbol-->
                                                            <div
                                                                class="symbol symbol-45 symbol-light-danger mr-4 flex-shrink-0"
                                                            >
                                                                <div class="symbol-label">
                                                                    <span
                                                                        class="svg-icon svg-icon-lg svg-icon-danger"
                                                                    >
                                                                        <inline-svg
                                                                            src="/media/svg/icons/Design/Target.svg"
                                                                        ></inline-svg>
                                                                    </span>
                                                                </div>
                                                            </div>
                                                            <!--end::Symbol-->
                                                            <!--begin::Title-->
                                                            <div>
                                                                <div
                                                                    class="font-size-h4 text-dark-75 font-weight-bolder"
                                                                >
                                                                    {{
                                                                        dashboard.globals
                                                                            .competitor_accounts
                                                                    }}
                                                                </div>
                                                                <div
                                                                    class="font-size-sm text-muted font-weight-bold mt-1"
                                                                >
                                                                    Competitor Accounts Monitored
                                                                </div>
                                                            </div>
                                                            <!--end::Title-->
                                                        </div>
                                                    </div>
                                                    <!--end::Item-->
                                                </div>
                                            </div>
                                            <!--end::Items-->
                                        </div>
                                        <!--end::Body-->
                                    </div>
                                    <!-- FIXME - Section breaker -->
                                </div>
                            </div>
                            <div class="card card-custom gutter-b">
                                <div class="card-body">
                                    <!-- FIXME - End section breaker -->
                                    <b-button-toolbar>
                                        <b-button-group class="mx-auto">
                                            <b-button
                                                :variant="buttonVariant('all')"
                                                @click="setSelectAccountType('all')"
                                                >All</b-button
                                            >
                                            <b-button
                                                :variant="buttonVariant('campaign')"
                                                @click="setSelectAccountType('campaign')"
                                                >Campaigns</b-button
                                            >
                                            <b-button
                                                :variant="buttonVariant('scanner')"
                                                @click="setSelectAccountType('scanner')"
                                                >Tracked accounts</b-button
                                            >
                                        </b-button-group>
                                    </b-button-toolbar>
                                    <template v-if="modals.selectAccount.type == 'scanner'">
                                        <b-button-toolbar class="gutter-t">
                                            <b-button-group class="mx-auto">
                                                <b-button
                                                    :variant="buttonSubVariant('all')"
                                                    @click="setSelectAccountSubType('all')"
                                                    >All</b-button
                                                >
                                                <b-button
                                                    :variant="buttonSubVariant('personal')"
                                                    @click="setSelectAccountSubType('personal')"
                                                    >Personal</b-button
                                                >
                                                <b-button
                                                    :variant="buttonSubVariant('competitor')"
                                                    @click="setSelectAccountSubType('competitor')"
                                                    >Competitor</b-button
                                                >
                                            </b-button-group>
                                        </b-button-toolbar>
                                    </template>
                                    <!--begin::Nav Tabs-->
                                    <div class="nav nav-danger row-paddingless row" role="tablist">
                                        <template v-for="account in dashboard.accounts">
                                            <template
                                                v-if="
                                                    modalSelectAccountDisplay(
                                                        account.type,
                                                        account.subtype
                                                    )
                                                "
                                            >
                                                <template
                                                    v-if="
                                                        handleAccountSearch(account.data.username)
                                                    "
                                                >
                                                    <div class="col-md-2 col-6 nav-item p-2">
                                                        <a
                                                            class="nav-link border py-10 d-flex flex-grow-1 rounded flex-column align-items-center hover-pointer"
                                                            @click="loadAccount(account)"
                                                            data-toggle="pill"
                                                        >
                                                            <!-- <span class="nav-icon py-2 w-auto">
                                                        <span class="svg-icon svg-icon-3x"> -->
                                                            <div
                                                                class="symbol symbol-100 symbol-light mt-1"
                                                            >
                                                                <span class="symbol symbol-100">
                                                                    <img
                                                                        :src="
                                                                            account.data.profilepic
                                                                        "
                                                                        class="align-self-end"
                                                                    />
                                                                </span>
                                                            </div>
                                                            <!-- </span>
                                                    </span> -->
                                                            <span
                                                                class="nav-text font-size-lg py-2 font-weight-bolder text-center"
                                                                >@{{ account.data.username }}</span
                                                            >
                                                            <template v-if="account.data.followers">
                                                                <sub
                                                                    >{{
                                                                        abbreviateNumber(
                                                                            account.data.followers
                                                                        )
                                                                    }}
                                                                    followers</sub
                                                                >
                                                            </template>
                                                        </a>
                                                    </div>
                                                </template>
                                            </template>
                                        </template>
                                    </div>
                                    <!--end::Nav Tabs-->
                                    <!--begin::Nav Content-->
                                    <div class="tab-content m-0 p-0">
                                        <div
                                            class="tab-pane active"
                                            id="forms_widget_tab_1"
                                            role="tabpanel"
                                        ></div>
                                        <div
                                            class="tab-pane"
                                            id="forms_widget_tab_2"
                                            role="tabpanel"
                                        ></div>
                                        <div
                                            class="tab-pane"
                                            id="forms_widget_tab_3"
                                            role="tabpanel"
                                        ></div>
                                        <div
                                            class="tab-pane"
                                            id="forms_widget_tab_4"
                                            role="tabpanel"
                                        ></div>
                                        <div
                                            class="tab-pane"
                                            id="forms_widget_tab_6"
                                            role="tabpanel"
                                        ></div>
                                    </div>
                                    <!--end::Nav Content-->
                                </div>
                                <!--end::Body-->
                            </div>
                            <!--begin::Nav Panel Widget 1-->
                        </template>
                    </template>
                </div>
            </div>
        </template>

        <!-- SECTION - Account section -->
        <template v-if="currentSection == 'account'">
            <template v-if="modals.selectAccount.isLoading">
                <PagePreloader>account</PagePreloader>
            </template>
            <template v-if="!modals.selectAccount.isLoading">
                <CustomAlerts :errors="accountErrors"></CustomAlerts>
                <template
                    v-if="
                        currentAccount.user.globals.is_campaign &&
                        !currentAccount.user.globals.has_facebook
                    "
                >
                    <div
                        role="alert"
                        aria-live="polite"
                        aria-atomic="true"
                        class="alert alert-dismissible alert-warning"
                    >
                        Instagram has recently added some significant limitations to account
                        scanners such as Hashtastic. As a result, Campaign scanning may be slower
                        than usual on accounts that don't have a linked Facebook Account. In order
                        to improve the quality of the results on your Campaign, you will need to
                        link your Facebook Business account by clicking on the button below.
                        Monitoring of your hashtag performance will then resume normally. Apologies
                        for the inconvenience while we work with Instagram on bringing everything
                        back to normal and remove the need for this workaround.
                        <b-button class="btn btn-primary btn-sm" @click="openAddAccountModal()"
                            >Link Facebook Account</b-button
                        >
                    </div>
                </template>
                <!-- NOTE - Top section -->
                <div class="row">
                    <!-- Print header -->
                    <template v-if="isEnterprise">
                        <div class="col-12 gutter-b">
                            <EnterpriseLogoDisplay>
                                <div class="gutter-b">
                                    <h3>Hashtastic Performance Report</h3>
                                    <h6>
                                        <sub>Generated on {{ Date() }}</sub>
                                    </h6>
                                </div>
                            </EnterpriseLogoDisplay>
                        </div>
                    </template>

                    <!-- NOTE - HEADER -->
                    <div class="col-xl-4 col-md-4">
                        <a
                            class="btn btn-light-success font-weight-bold py-3 px-6 mb-2 text-center btn-block d-print-none"
                            @click="openAccountSelectionSection()"
                            ><i class="fas fa-undo-alt pr-2"></i>Scan other account</a
                        >
                        <div class="card card-custom gutter-b">
                            <!-- <div class="card-header border-0 pt-5">
                            <div class="card-title">
                                <div class="card-label">
                                    <div class="font-weight-bolder">Weekly Sales Stats</div>
                                    <div class="font-size-sm text-muted mt-2">890,344 Sales</div>
                                </div>
                            </div>
                            <div class="card-toolbar">
                                
                            </div>
                        </div> -->
                            <!--begin::Body-->
                            <div class="card-body pt-4">
                                <!--begin::Toolbar-->
                                <!-- <div class="d-flex justify-content-end">
                                    <div class="dropdown dropdown-inline">
                                        <b-dropdown
                                            size="sm"
                                            variant="link"
                                            toggle-class="custom-v-dropdown btn btn-clean btn-hover-light-primary btn-sm btn-icon"
                                            no-caret
                                            right
                                            no-flip
                                        >
                                            <template v-slot:button-content>
                                                <i class="ki ki-bold-more-hor"></i>
                                            </template>
                                            <div class="navi navi-hover min-w-md-250px">
                                                <b-dropdown-text tag="div" class="navi-item">
                                                    <a class="navi-link">
                                                        <span class="navi-icon">
                                                            <i class="flaticon2-group"></i>
                                                        </span>
                                                        <span class="navi-text">New Group</span>
                                                    </a>
                                                </b-dropdown-text>
                                                <b-dropdown-text tag="div" class="navi-item">
                                                    <a class="navi-link">
                                                        <span class="navi-icon">
                                                            <i class="flaticon2-open-text-book"></i>
                                                        </span>
                                                        <span class="navi-text">Contacts</span>
                                                    </a>
                                                </b-dropdown-text>
                                                <b-dropdown-text tag="div" class="navi-item">
                                                    <a class="navi-link">
                                                        <span class="navi-icon">
                                                            <i class="flaticon2-rocket-1"></i>
                                                        </span>
                                                        <span class="navi-text">Groups</span>
                                                        <span class="navi-link-badge">
                                                            <span
                                                                class="
                                                                    label
                                                                    label-light-primary
                                                                    label-inline
                                                                    font-weight-bold
                                                                "
                                                                >new</span
                                                            >
                                                        </span>
                                                    </a>
                                                </b-dropdown-text>
                                                <b-dropdown-text tag="div" class="navi-item">
                                                    <a class="navi-link">
                                                        <span class="navi-icon">
                                                            <i class="flaticon2-bell-2"></i>
                                                        </span>
                                                        <span class="navi-text">Calls</span>
                                                    </a>
                                                </b-dropdown-text>
                                                <b-dropdown-text tag="div" class="navi-item">
                                                    <a class="navi-link">
                                                        <span class="navi-icon">
                                                            <i class="flaticon2-dashboard"></i>
                                                        </span>
                                                        <span class="navi-text">Settings</span>
                                                    </a>
                                                </b-dropdown-text>
                                                <b-dropdown-text
                                                    tag="div"
                                                    class="navi-separator my-3"
                                                ></b-dropdown-text>
                                                <b-dropdown-text tag="div" class="navi-item">
                                                    <a class="navi-link">
                                                        <span class="navi-icon">
                                                            <i class="flaticon2-protected"></i>
                                                        </span>
                                                        <span class="navi-text">Help</span>
                                                    </a>
                                                </b-dropdown-text>
                                                <b-dropdown-text tag="div" class="navi-item">
                                                    <a class="navi-link">
                                                        <span class="navi-icon">
                                                            <i class="flaticon2-bell-2"></i>
                                                        </span>
                                                        <span class="navi-text">Privacy</span>
                                                        <span class="navi-link-badge">
                                                            <span
                                                                class="
                                                                    label
                                                                    label-light-danger
                                                                    label-rounded
                                                                    font-weight-bold
                                                                "
                                                            >
                                                                5
                                                            </span>
                                                        </span>
                                                    </a>
                                                </b-dropdown-text>
                                            </div>
                                        </b-dropdown>
                                    </div>
                                </div> -->
                                <!--end::Toolbar-->
                                <!--begin::User-->
                                <div class="d-flex align-items-center">
                                    <div
                                        class="symbol symbol-60 symbol-xxl-100 mr-5 align-self-start align-self-xxl-center"
                                    >
                                        <div
                                            class="symbol-label d-print-none"
                                            :style="{
                                                backgroundImage:
                                                    'url(' + currentAccount.user.profilepic + ')',
                                            }"
                                        ></div>
                                        <img
                                            class="d-none d-print-block symbol symbol-60 img img-responsive"
                                            :src="currentAccount.user.profilepic"
                                        />
                                        <i class="symbol-badge bg-success"></i>
                                    </div>
                                    <div>
                                        <a
                                            href="#"
                                            class="font-weight-bold font-size-h5 text-dark-75 text-hover-primary"
                                            >{{ currentAccount.user.username }}</a
                                        >
                                        <div class="text-muted">{{ currentAccount.user.name }}</div>

                                        <div class="mt-2">
                                            <template v-if="currentAccount.user.external_url">
                                                <a
                                                    :href="currentAccount.user.external_url"
                                                    target="_blank"
                                                    class="btn btn-sm btn-primary font-weight-bold mr-2 py-2 px-3 px-xxl-5 my-1"
                                                    >Website</a
                                                >
                                            </template>
                                            <a
                                                :href="
                                                    'https://instagram.com/' +
                                                    currentAccount.user.username
                                                "
                                                target="_blank"
                                                class="btn btn-sm btn-success font-weight-bold py-2 px-3 px-xxl-5 my-1"
                                                >Instagram</a
                                            >
                                        </div>
                                    </div>
                                </div>
                                <!--end::User-->
                                <!--begin::Contact-->
                                <!-- <div class="pt-8 pb-6">
                                <div class="d-flex align-items-center justify-content-between mb-2">
                                    <span class="font-weight-bold mr-2">Email:</span>
                                    <a href="#" class="text-muted text-hover-primary"
                                        >matt@fifestudios.com</a
                                    >
                                </div>
                                <div class="d-flex align-items-center justify-content-between mb-2">
                                    <span class="font-weight-bold mr-2">Phone:</span>
                                    <span class="text-muted">44(76)34254578</span>
                                </div>
                                <div class="d-flex align-items-center justify-content-between">
                                    <span class="font-weight-bold mr-2">Location:</span>
                                    <span class="text-muted">Melbourne</span>
                                </div>
                            </div> -->
                                <!--end::Contact-->
                                <!--begin::Contact-->
                                <div class="pt-6 pb-6 account-bio">
                                    {{ currentAccount.user.bio }}
                                </div>
                                <!--end::Contact-->
                                <!-- <a
                                    href="#"
                                    class="
                                        btn btn-light-success
                                        font-weight-bold
                                        py-3
                                        px-6
                                        mb-2
                                        text-center
                                        btn-block
                                    "
                                    >Profile Overview</a
                                > -->
                            </div>
                            <!--end::Body-->
                            <div class="card-footer d-print-none">
                                <!-- <a
                                    href="#"
                                    class="
                                        btn btn-light-danger
                                        font-weight-bold
                                        py-3
                                        px-6
                                        mb-2
                                        text-center
                                        btn-block
                                    "
                                    >Profile Overview</a
                                > -->
                                <div role="group" class="btn-group col">
                                    <template v-if="isEnterprise">
                                        <button
                                            type="button"
                                            class="btn btn-sm btn-light-primary font-weight-bolder text-uppercase"
                                            @click="printReport()"
                                        >
                                            <i class="fas fa-download ml-2"></i> Export
                                        </button>
                                    </template>

                                    <template v-if="currentAccount.user.globals.is_campaign">
                                        <button
                                            @click="openStopCampaignModal()"
                                            type="button"
                                            class="btn btn-sm btn-light-danger font-weight-bolder text-uppercase mr-2 btn-secondary"
                                        >
                                            Stop Campaign
                                        </button>
                                    </template>
                                    <template v-else>
                                        <template v-if="currentAccount.user.globals.is_monitoring">
                                            <button
                                                @click="openStopAccountMonitoringModal()"
                                                type="button"
                                                class="btn btn-sm btn-light-danger font-weight-bolder text-uppercase mr-2 btn-secondary"
                                            >
                                                Stop Monitoring
                                            </button>
                                        </template>
                                    </template>
                                </div>
                            </div>
                        </div>
                    </div>
                    <!-- NOTE - End header -->

                    <!-- NOTE - Start statistics -->
                    <div class="col-xl-8 col-md-8">
                        <div class="row">
                            <div class="col-xl-3 col-md-3">
                                <div class="card card-custom gutter-b" style="height: 150px">
                                    <div class="card-body">
                                        <span class="svg-icon svg-icon-3x svg-icon-success">
                                            <!--begin::Svg Icon | path:/metronic/theme/html/demo2/dist/assets/media/svg/icons/Communication/Group.svg-->
                                            <inline-svg
                                                src="/media/svg/icons/Communication/Group.svg"
                                            ></inline-svg>
                                            <!--end::Svg Icon-->
                                        </span>
                                        <div class="text-dark font-weight-bolder font-size-h2 mt-3">
                                            {{ addCommaToNumber(currentAccount.user.followers) }}
                                        </div>
                                        <a
                                            href="#"
                                            class="text-muted text-hover-primary font-weight-bold font-size-lg mt-1"
                                            >Followers</a
                                        >
                                    </div>
                                </div>
                            </div>
                            <div class="col-xl-3 col-md-3">
                                <div class="card card-custom gutter-b" style="height: 150px">
                                    <div class="card-body">
                                        <span class="svg-icon svg-icon-3x svg-icon-primary">
                                            <!--begin::Svg Icon | path:/metronic/theme/html/demo2/dist/assets/media/svg/icons/Communication/Group.svg-->
                                            <inline-svg
                                                src="/media/svg/icons/Communication/Group.svg"
                                            ></inline-svg>
                                            <!--end::Svg Icon-->
                                        </span>
                                        <div class="text-dark font-weight-bolder font-size-h2 mt-3">
                                            {{ addCommaToNumber(currentAccount.user.followings) }}
                                        </div>
                                        <a
                                            href="#"
                                            class="text-muted text-hover-primary font-weight-bold font-size-lg mt-1"
                                            >Followings</a
                                        >
                                    </div>
                                </div>
                            </div>
                            <div class="col-xl-3 col-md-3">
                                <div class="card card-custom gutter-b" style="height: 150px">
                                    <div class="card-body">
                                        <span class="svg-icon svg-icon-3x svg-icon-primary ml-n2">
                                            <!--begin::Svg Icon | path:/metronic/theme/html/demo2/dist/assets/media/svg/icons/Layout/Layout-4-blocks.svg-->
                                            <inline-svg
                                                src="/media/svg/icons/Layout/Layout-4-blocks.svg"
                                            ></inline-svg>
                                            <!--end::Svg Icon-->
                                        </span>
                                        <div class="text-dark font-weight-bolder font-size-h2 mt-3">
                                            {{ addCommaToNumber(currentAccount.user.postcount) }}
                                        </div>
                                        <a
                                            href="#"
                                            class="text-muted text-hover-primary font-weight-bold font-size-lg mt-1"
                                            >Posts</a
                                        >
                                    </div>
                                </div>
                            </div>
                            <div class="col-xl-3 col-md-3">
                                <div class="card card-custom gutter-b" style="height: 150px">
                                    <div class="card-body">
                                        <span class="svg-icon svg-icon-3x svg-icon-primary">
                                            <!--begin::Svg Icon | path:/metronic/theme/html/demo2/dist/assets/media/svg/icons/Communication/Group.svg-->
                                            <inline-svg
                                                src="/media/svg/icons/Shopping/Chart-pie.svg"
                                            ></inline-svg>
                                            <!--end::Svg Icon-->
                                        </span>
                                        <div class="text-dark font-weight-bolder font-size-h2 mt-3">
                                            {{
                                                calculateEngagement(
                                                    currentAccount.user.avglikes,
                                                    currentAccount.user.followers
                                                )
                                            }}
                                        </div>
                                        <a
                                            href="#"
                                            class="text-muted text-hover-primary font-weight-bold font-size-lg mt-1"
                                            >Engagement</a
                                        >
                                    </div>
                                </div>
                            </div>
                            <!-- NOTE - Start selected hashtags -->
                            <div class="col-xl-12">
                                <div class="card card-custom gutter-b d-print-none">
                                    <div class="card-header">
                                        <div class="card-title">
                                            <span class="card-icon">
                                                <i class="fas fa-hashtag text-success"></i>
                                            </span>
                                            <h3 class="card-label">Selected Hashtags</h3>
                                        </div>
                                        <div class="card-toolbar">
                                            <span
                                                class="btn btn-bold btn-sm"
                                                :class="
                                                    selectedHashtags.length >= 30
                                                        ? 'btn-outline-danger'
                                                        : 'btn-outline-primary'
                                                "
                                            >
                                                {{ selectedHashtags.length }} hashtags selected
                                            </span>
                                        </div>
                                    </div>
                                    <div class="card-body">
                                        <div v-if="selectedHashtags.length < 1">
                                            <infoBox
                                                text="You currently haven't selected any hashtags. Use the tables below to make your selection."
                                            ></infoBox>
                                        </div>

                                        <div v-if="selectedHashtags.length > 0">
                                            <div class="row">
                                                <div
                                                    class="col-xl-12 btn-group"
                                                    style="flex-wrap: wrap"
                                                >
                                                    <div
                                                        v-for="selectedHashtag in selectedHashtags"
                                                    >
                                                        <span
                                                            class="btn btn-sm btn-font-sm font-weight-bold btn-light-primary m-1"
                                                            >#{{ selectedHashtag }}</span
                                                        >
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div class="card-footer row" v-if="selectedHashtags.length > 0">
                                        <!-- NOTE - Copy to Clipboard button -->
                                        <buttonCopyToClipboard
                                            :propHashtags="selectedHashtagsHashed"
                                            class="col-md-3 col-12"
                                        ></buttonCopyToClipboard>
                                        <!-- NOTE - Create new hashtag set -->
                                        <buttonCreateNewHashtagSet
                                            :propHashtags="selectedHashtagsHashed"
                                            @emitPassMessage="handleEmitMessages"
                                            class="col-md-3 col-12"
                                        >
                                        </buttonCreateNewHashtagSet>
                                        <!-- NOTE - Add to existing hashtag set -->
                                        <buttonAddToExistingHashtagSet
                                            @emitPassMessage="handleEmitMessages"
                                            :propHashtags="selectedHashtagsHashed"
                                            class="col-md-3 col-12"
                                        ></buttonAddToExistingHashtagSet>
                                    </div>
                                </div>
                            </div>
                            <!-- NOTE - End selected hashtags -->
                        </div>
                    </div>
                    <!-- NOTE - End statistics -->
                </div>
                <!-- NOTE - End top section -->

                <!-- NOTE - Navigation -->
                <div class="card card-custom gutter-b d-print-none">
                    <b-nav pills fill justified>
                        <b-nav-item :active="isCurrentTab('posts')" @click="switchTab('posts')">
                            <span :class="isCurrentTabSvgIcon('posts')">
                                <inline-svg
                                    src="/media/svg/icons/Layout/Layout-4-blocks.svg"
                                ></inline-svg>
                            </span>

                            Posts</b-nav-item
                        >
                        <b-nav-item
                            :active="isCurrentTab('campaign')"
                            @click="switchTab('campaign')"
                        >
                            <span :class="isCurrentTabSvgIcon('campaign')">
                                <inline-svg src="/media/svg/icons/Design/Select.svg"></inline-svg>
                            </span>
                            Hashtag Campaign
                        </b-nav-item>
                        <b-nav-item
                            :active="isCurrentTab('insights')"
                            @click="switchTab('insights')"
                        >
                            <span :class="isCurrentTabSvgIcon('insights')">
                                <inline-svg
                                    src="/media/svg/icons/Shopping/Chart-bar1.svg"
                                ></inline-svg>
                            </span>
                            Insights
                        </b-nav-item>
                        <b-nav-item :active="isCurrentTab('growth')" @click="switchTab('growth')">
                            <span :class="isCurrentTabSvgIcon('growth')">
                                <inline-svg src="/media/svg/icons/Shopping/Sort3.svg"></inline-svg>
                            </span>
                            Growth</b-nav-item
                        >
                    </b-nav>
                </div>

                <!-- NOTE - End navigation -->

                <!-- NOTE - Tabs -->
                <!-- NOTE - Posts -->
                <div v-if="currentTab == 'posts'" key="tab_posts" class="row">
                    <!-- NOTE - Preloader -->
                    <template v-if="currentAccount.postsIsLoading">
                        <div class="col-xl-12">
                            <PagePreloader>posts</PagePreloader>
                        </div>
                    </template>

                    <template v-if="currentAccount.posts.data">
                        <template v-if="currentAccount.posts.data.data.length">
                            <template v-for="post in currentAccount.posts.data.data">
                                <AccountDashboardPost
                                    :currentAccount="currentAccount"
                                    :post="post"
                                    @clickedOnRow="getAllSelectedItems"
                                ></AccountDashboardPost>
                            </template>
                            <!-- END post loop -->
                            <template v-if="morePostsToLoad()">
                                <div class="col-xl-12">
                                    <button
                                        type="button"
                                        class="btn btn-white btn-wide col-12 gutter-b d-print-none"
                                        :disabled="currentAccount.postsIsLoading"
                                        :class="postsButtonLoading()"
                                        @click="
                                            loadPosts(
                                                currentAccount.user.userid,
                                                currentAccount.postsCurrentPage + 1
                                            )
                                        "
                                    >
                                        <i class="fas fa-redo text-success"></i>
                                        Load more results...
                                    </button>
                                </div>
                            </template>
                        </template>
                        <template v-else>
                            <div class="card card-body card-custom">
                                <p class="lead">
                                    There are currently no posts to display. Hashtastic will
                                    automatically scrape all new posts submitted to this account.
                                </p>
                            </div>
                        </template>
                    </template>

                    <!-- NOTE - End posts -->
                </div>

                <!-- NOTE - Campaign -->
                <div v-if="currentTab == 'campaign'" key="tab_campaign" class="row">
                    <template v-if="currentAccount.campaignResultsIsLoading">
                        <div class="col-xl-12">
                            <PagePreloader>campaign results</PagePreloader>
                        </div>
                    </template>
                    <template v-if="!currentAccount.campaignResultsIsLoading">
                        <template v-if="currentAccount.campaignResults">
                            <template
                                v-if="!currentAccount.campaignResults.globals.campaign_exists"
                            >
                                <div class="col-xl-12">
                                    <div class="card card-custom">
                                        <div class="card-body">
                                            <p class="lead">
                                                There are currently no Hashtag Performance
                                                statistics available for this account yet.
                                            </p>
                                            <p>
                                                Hashtag Performance Campaigns enable you to identify
                                                precisely which hashtags are working best for you,
                                                which ones are driving traffic to your page, and
                                                which ones you should remove.
                                            </p>
                                            <b-button
                                                class="btn btn-light-success btn-lg pr-2"
                                                @click="openAddCampaignModal()"
                                                ><i class="fas fa-user-plus"></i>Start
                                                Campaign</b-button
                                            >
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <template
                                    v-if="
                                        !currentAccount.campaignResults.globals.campaign_is_active
                                    "
                                >
                                    <div class="col-xl-12">
                                        <div
                                            class="alert alert-custom alert-notice alert-light-warning fade show mb-5"
                                            role="alert"
                                        >
                                            <div class="alert-icon">
                                                <i class="flaticon-warning"></i>
                                            </div>
                                            <div class="alert-text">
                                                <p>
                                                    This Campaign is currently inactive. The results
                                                    displayed below are the results obtained while
                                                    the campaign was still active.
                                                </p>
                                                <b-button
                                                    class="btn btn-light btn-sm"
                                                    @click="openAddCampaignModal()"
                                                    ><i class="fas fa-user-plus pr-2"></i>Start
                                                    Campaign</b-button
                                                >
                                            </div>
                                            <div class="alert-close">
                                                <button
                                                    type="button"
                                                    class="close"
                                                    data-dismiss="alert"
                                                    aria-label="Close"
                                                >
                                                    <span aria-hidden="true">
                                                        <i class="ki ki-close"></i>
                                                    </span>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                                <!-- NOTE - Start Campaign overall progress -->
                                <div class="col-xl-4 col-md-4">
                                    <!--begin::Mixed Widget 18-->
                                    <div class="card card-custom gutter-b card-stretch">
                                        <!--begin::Header-->
                                        <div class="card-header border-0 pt-5">
                                            <div class="card-title font-weight-bolder">
                                                <div class="card-label">
                                                    Hashtag Campaign Progress
                                                    <div class="font-size-sm text-muted mt-2">
                                                        Since
                                                        {{
                                                            currentAccount.campaignResults.progress
                                                                .startdate
                                                        }}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <!--end::Header-->
                                        <!--begin::Body-->
                                        <div class="card-body">
                                            <!--begin::Items-->
                                            <div>
                                                <!--begin::Widget Item-->
                                                <div class="d-flex align-items-center mb-8">
                                                    <!--begin::Symbol-->
                                                    <div
                                                        class="symbol symbol-circle symbol-40 symbol-light mr-3 flex-shrink-0"
                                                    >
                                                        <div class="symbol-label">
                                                            <span
                                                                class="svg-icon svg-icon-lg svg-icon-success"
                                                            >
                                                                <inline-svg
                                                                    src="/media/svg/icons/Communication/Group.svg"
                                                                ></inline-svg>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <!--end::Symbol-->
                                                    <!--begin::Title-->
                                                    <div>
                                                        <a
                                                            class="font-size-h6 text-dark-75 text-hover-primary font-weight-bolder"
                                                            >{{
                                                                currentAccount.campaignResults
                                                                    .progress.followerdelta
                                                            }}
                                                            new followers</a
                                                        >
                                                        <!-- <div
                                                            class="
                                                                font-size-sm
                                                                text-muted
                                                                font-weight-bold
                                                                mt-1
                                                            "
                                                        >
                                                            xxx
                                                        </div> -->
                                                    </div>
                                                    <!--end::Title-->
                                                </div>
                                                <!--end::Widget Item-->
                                                <!--begin::Widget Item-->
                                                <div class="d-flex align-items-center mb-8">
                                                    <!--begin::Symbol-->
                                                    <div
                                                        class="symbol symbol-circle symbol-40 symbol-light mr-3 flex-shrink-0"
                                                    >
                                                        <div class="symbol-label">
                                                            <span
                                                                class="svg-icon svg-icon-lg svg-icon-success"
                                                            >
                                                                <inline-svg
                                                                    src="/media/svg/icons/Layout/Layout-4-blocks.svg"
                                                                ></inline-svg>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <!--end::Symbol-->
                                                    <!--begin::Title-->
                                                    <div>
                                                        <a
                                                            class="font-size-h6 text-dark-75 text-hover-primary font-weight-bolder"
                                                            >{{
                                                                currentAccount.campaignResults
                                                                    .progress.trackedpostcount
                                                            }}
                                                            new posts</a
                                                        >
                                                        <div
                                                            class="font-size-sm text-muted font-weight-bold mt-1"
                                                        >
                                                            Tracked by Hashtastic
                                                        </div>
                                                    </div>
                                                    <!--end::Title-->
                                                </div>
                                                <!--end::Widget Item-->
                                                <!--begin::Widget Item-->
                                                <div class="d-flex align-items-center mb-8">
                                                    <!--begin::Symbol-->
                                                    <div
                                                        class="symbol symbol-circle symbol-40 symbol-light mr-3 flex-shrink-0"
                                                    >
                                                        <div class="symbol-label">
                                                            <span
                                                                class="svg-icon svg-icon-lg svg-icon-success"
                                                            >
                                                                <inline-svg
                                                                    src="/media/svg/icons/Text/Menu.svg"
                                                                ></inline-svg>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <!--end::Symbol-->
                                                    <!--begin::Title-->
                                                    <div>
                                                        <a
                                                            class="font-size-h6 text-dark-75 text-hover-primary font-weight-bolder"
                                                            >{{
                                                                currentAccount.campaignResults
                                                                    .performance.statistics
                                                                    .totalrankedhashtags
                                                            }}
                                                            Ranked Hashtags</a
                                                        >
                                                        <div
                                                            class="font-size-sm text-muted font-weight-bold mt-1"
                                                        >
                                                            Identified by Hashtastic
                                                        </div>
                                                    </div>
                                                    <!--end::Title-->
                                                </div>
                                                <!--end::Widget Item-->
                                                <!--begin::Widget Item-->
                                                <div class="d-flex align-items-center">
                                                    <!--begin::Symbol-->
                                                    <div
                                                        class="symbol symbol-circle symbol-40 symbol-light mr-3 flex-shrink-0"
                                                    >
                                                        <div class="symbol-label">
                                                            <span
                                                                class="svg-icon svg-icon-lg svg-icon-success"
                                                            >
                                                                <inline-svg
                                                                    src="/media/svg/icons/Home/Timer.svg"
                                                                ></inline-svg>
                                                            </span>
                                                        </div>
                                                    </div>
                                                    <!--end::Symbol-->
                                                    <!--begin::Title-->
                                                    <div>
                                                        <a
                                                            class="font-size-h6 text-dark-75 text-hover-primary font-weight-bolder"
                                                            >{{
                                                                currentAccount.campaignResults
                                                                    .progress.dayssincestart
                                                            }}
                                                            days running</a
                                                        >
                                                        <div
                                                            class="font-size-sm text-muted font-weight-bold mt-1"
                                                        >
                                                            Since Campaign start
                                                        </div>
                                                    </div>
                                                    <!--end::Title-->
                                                </div>
                                                <!--end::Widget Item-->
                                            </div>
                                            <!--end::Items-->
                                        </div>
                                        <!--end::Body-->
                                    </div>
                                    <!--end::Mixed Widget 18-->
                                </div>
                                <!-- NOTE - End Campaign overall progress -->
                                <!-- NOTE - Start Campaign Summary -->
                                <div class="col-xl-8 col-md-8">
                                    <div class="card card-custom card-stretch gutter-b">
                                        <div class="card-body">
                                            <div class="row">
                                                <div class="col-lg-6 col-md-12">
                                                    <div id="chart">
                                                        <h4>Hashtag Performance</h4>
                                                        <h6 class="text-muted">
                                                            Hashtag Performance by section
                                                        </h6>
                                                        <apexchart
                                                            type="donut"
                                                            :options="
                                                                currentAccount.campaignResultsChartOptions
                                                            "
                                                            :series="
                                                                currentAccount.campaignResultsChartSeries
                                                            "
                                                            class="p-2"
                                                        ></apexchart>
                                                    </div>
                                                </div>
                                                <div class="col-lg-6 col-md-12">
                                                    <div
                                                        class="alert alert-custom alert-white shadow-sm alert-elevate"
                                                        role="alert"
                                                    >
                                                        <div class="alert-icon">
                                                            <i
                                                                class="fas fa-fire-alt text-success"
                                                            ></i>
                                                        </div>
                                                        <div class="alert-text">
                                                            You've ranked on
                                                            <span class="font-weight-bolder"
                                                                >{{
                                                                    currentAccount.campaignResults
                                                                        .performance.statistics
                                                                        .totalrankedhashtags
                                                                }}
                                                                hashtags</span
                                                            >
                                                            you've used since the
                                                            <span class="font-weight-bolder">{{
                                                                currentAccount.campaignResults
                                                                    .progress.startdate
                                                            }}</span
                                                            >, including
                                                            <span class="font-weight-bolder"
                                                                >{{
                                                                    currentAccount.campaignResults
                                                                        .performance.statistics
                                                                        .totaltop9hashtags
                                                                }}
                                                                hashtags</span
                                                            >
                                                            that ranked in the top 9 positions.
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-xl-12">
                                    <div class="card card-custom gutter-b">
                                        <div class="card-header">
                                            <div class="card-title">
                                                <span class="card-icon">
                                                    <i
                                                        class="fas fa-tachometer-alt text-success"
                                                    ></i>
                                                </span>
                                                <h3 class="card-label">Performance Summary</h3>
                                            </div>
                                        </div>
                                        <div class="card-body pt-0">
                                            <div class="">
                                                <div class="row">
                                                    <div
                                                        v-if="
                                                            currentAccount.campaignResults
                                                                .performance
                                                                .individual_hashtag_performance
                                                                .length > 0
                                                        "
                                                        class="col-12"
                                                    >
                                                        <InfoBox
                                                            class="mt-5"
                                                            text="This table indicates the number of times your posts ranked within the 'top posts' section of a hashtag feed, as well as how well they ranked."
                                                        ></InfoBox>

                                                        <!-- Select / Deselect toolbar -->
                                                        <div class="card card-custom">
                                                            <div class="card-header">
                                                                <div class="card-title"></div>
                                                                <div
                                                                    class="card-toolbar d-print-none"
                                                                >
                                                                    <div class="btn-group">
                                                                        <b-button
                                                                            variant="light-primary"
                                                                            size="sm"
                                                                            @click="
                                                                                selectAll('summary')
                                                                            "
                                                                            >Select All</b-button
                                                                        >
                                                                        <b-button
                                                                            variant="light-danger"
                                                                            size="sm"
                                                                            @click="
                                                                                deselectAll(
                                                                                    'summary'
                                                                                )
                                                                            "
                                                                            >Deselect All</b-button
                                                                        >
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>

                                                        <!-- Table -->
                                                        <b-table
                                                            style="overflow: scroll"
                                                            striped
                                                            hover
                                                            selectable
                                                            bordered
                                                            select-mode="multi"
                                                            @row-clicked="
                                                                (item) => onRowClicked(item)
                                                            "
                                                            :fields="fieldsResultsSummary"
                                                            :items="
                                                                currentAccount.campaignResults
                                                                    .performance
                                                                    .individual_hashtag_performance
                                                            "
                                                            :per-page="15"
                                                            :current-page="summaryCurrentPage"
                                                            sortBy="top9count"
                                                            :sortDesc="true"
                                                            :sort-compare="customTableSort"
                                                            responsive="sm"
                                                            ref="selectableTable"
                                                        >
                                                            <template
                                                                v-slot:cell(selected)="{
                                                                    item,
                                                                    field: { key },
                                                                }"
                                                            >
                                                                <template v-if="item[key]">
                                                                    <span aria-hidden="true"
                                                                        ><i
                                                                            class="far fa-check-square text-primary font-weight-bolder"
                                                                        ></i
                                                                    ></span>
                                                                    <span class="sr-only"
                                                                        >Selected</span
                                                                    >
                                                                </template>
                                                                <template v-else>
                                                                    <span aria-hidden="true"
                                                                        ><i
                                                                            class="far fa-square"
                                                                        ></i
                                                                    ></span>
                                                                    <span class="sr-only"
                                                                        >Not selected</span
                                                                    >
                                                                </template>
                                                                <!-- <b-checkbox v-model="item[key]"></b-checkbox> -->
                                                            </template>

                                                            <!-- Hashtag badge slot -->
                                                            <template
                                                                v-slot:cell(hashtagsBadged)="{
                                                                    item,
                                                                    field: { key },
                                                                }"
                                                            >
                                                                {{ item.hashtag }}
                                                                <span
                                                                    class="badge badge-warning"
                                                                    v-if="item.status == 'flagged'"
                                                                    >Flagged</span
                                                                >
                                                                <span
                                                                    class="badge badge-danger"
                                                                    v-if="item.status == 'banned'"
                                                                    >Banned</span
                                                                >
                                                            </template>
                                                        </b-table>
                                                        <b-pagination
                                                            v-model="summaryCurrentPage"
                                                            :total-rows="
                                                                currentAccount.campaignResults
                                                                    .performance
                                                                    .individual_hashtag_performance
                                                                    .length
                                                            "
                                                            :per-page="15"
                                                            align="center"
                                                            aria-controls="my-table"
                                                        ></b-pagination>
                                                    </div>
                                                    <div v-else class="col-12">
                                                        <InfoBox
                                                            class="mt-10"
                                                            text="There are no hashtags currently being tracked by Hashtastic for this account. Results will appear here as soon as you start posting on Instagram!"
                                                        ></InfoBox>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <!-- NOTE - End Campaign Summary -->
                            </template>
                        </template>
                    </template>
                    <!-- NOTE - End Campaign -->
                </div>

                <!-- NOTE - Insights tab -->
                <div v-if="currentTab == 'insights'" key="tab_insights" class="row">
                    <template v-if="currentAccount.fbinsightsIsLoading">
                        <div class="col-xl-12">
                            <PagePreloader>insights</PagePreloader>
                        </div>
                    </template>
                    <template v-if="!currentAccount.fbinsightsIsLoading">
                        <template v-if="!isEmptyObject(currentAccount.fbinsights)">
                            <div class="col-xl-3 gutter-b d-print-none">
                                <!--begin::Profile Card-->
                                <div class="card card-custom">
                                    <div class="card-header py-3">
                                        <div class="card-title align-items-start flex-column">
                                            <h3 class="card-label font-weight-bolder text-dark">
                                                Insights Menu
                                            </h3>
                                            <span
                                                class="text-muted font-weight-bold font-size-sm mt-1"
                                                >View your Instagram Insights</span
                                            >
                                        </div>
                                    </div>
                                    <!--begin::Body-->
                                    <div class="card-body pt-4">
                                        <!--begin::Nav-->
                                        <div
                                            class="navi navi-bold navi-hover navi-active navi-link-rounded py-2"
                                        >
                                            <!-- NOTE - Growth -->
                                            <div class="navi-item mb-2">
                                                <a
                                                    @click="setInsightsPanel('growth')"
                                                    class="navi-link py-4"
                                                    :class="
                                                        currentInsightsPanel == 'growth'
                                                            ? 'active'
                                                            : ''
                                                    "
                                                >
                                                    <span class="navi-icon mr-2">
                                                        <span class="svg-icon">
                                                            <inline-svg
                                                                src="/media/svg/icons/Shopping/Chart-line1.svg"
                                                            ></inline-svg>
                                                        </span>
                                                    </span>
                                                    <span class="navi-text font-size-lg">
                                                        Account Growth
                                                    </span>
                                                </a>
                                            </div>

                                            <!-- NOTE - Demographics -->
                                            <div class="navi-item mb-2">
                                                <a
                                                    @click="setInsightsPanel('demographics')"
                                                    class="navi-link py-4"
                                                    :class="
                                                        currentInsightsPanel == 'demographics'
                                                            ? 'active'
                                                            : ''
                                                    "
                                                >
                                                    <span class="navi-icon mr-2">
                                                        <span class="svg-icon">
                                                            <inline-svg
                                                                src="/media/svg/icons/Communication/Group.svg"
                                                            ></inline-svg>
                                                        </span>
                                                    </span>
                                                    <span class="navi-text font-size-lg">
                                                        Audience Demographics
                                                    </span>
                                                </a>
                                            </div>

                                            <!-- NOTE - Posts -->
                                            <!-- <div class="navi-item mb-2">
                                                <a
                                                    @click="setInsightsPanel('posts')"
                                                    class="navi-link py-4"
                                                    :class="
                                                        currentInsightsPanel == 'posts'
                                                            ? 'active'
                                                            : ''
                                                    "
                                                >
                                                    <span class="navi-icon mr-2">
                                                        <span class="svg-icon">
                                                            <inline-svg
                                                                src="/media/svg/icons/Design/Image.svg"
                                                            ></inline-svg>
                                                        </span>
                                                    </span>
                                                    <span class="navi-text font-size-lg">
                                                        Posts [BETA]</span
                                                    >
                                                </a>
                                            </div> -->
                                        </div>
                                        <!--end::Nav-->
                                    </div>
                                    <!--end::Body-->
                                </div>
                                <!--end::Profile Card-->
                            </div>
                            <!--end::Aside-->

                            <!-- Start Groups -->
                            <transition-group name="fade-in-up" tag="div" class="col-xl-9">
                                <!-- NOTE - Growth Panel -->
                                <div v-if="currentInsightsPanel == 'growth'" key="1">
                                    <!-- NOTE - Impressions -->
                                    <template v-if="currentAccount.fbinsights.impressions.length">
                                        <!--begin::Card-->
                                        <div class="card card-custom gutter-b">
                                            <!--begin::Header-->
                                            <div class="card-header py-3">
                                                <div
                                                    class="card-title align-items-start flex-column"
                                                >
                                                    <h3
                                                        class="card-label font-weight-bolder text-dark"
                                                    >
                                                        Reach &amp; Impressions
                                                    </h3>
                                                    <span
                                                        class="text-muted font-weight-bold font-size-sm mt-1"
                                                        >Over the last 30 days</span
                                                    >
                                                </div>
                                            </div>
                                            <!--end::Header-->
                                            <!-- Body -->
                                            <div class="card-body">
                                                <apexchart
                                                    :options="
                                                        currentAccount.fbinsights_charts
                                                            .reachImpressionsOptions
                                                    "
                                                    :series="
                                                        currentAccount.fbinsights_charts
                                                            .reachImpressionsSeries
                                                    "
                                                    type="area"
                                                    height="300px"
                                                ></apexchart>
                                                <apexchart
                                                    :options="
                                                        currentAccount.fbinsights_charts
                                                            .reachImpressionsByMonthOptions
                                                    "
                                                    :series="
                                                        currentAccount.fbinsights_charts
                                                            .reachImpressionsByMonthSeries
                                                    "
                                                    type="area"
                                                    height="300px"
                                                ></apexchart>
                                            </div>
                                            <!-- End Body -->
                                        </div>
                                    </template>
                                    <!--end:: Card -->
                                </div>
                                <!--end:: Card -->

                                <!-- NOTE - Demographics Card -->
                                <div v-if="currentInsightsPanel == 'demographics'" key="2">
                                    <!-- NOTE - Empty displayer -->
                                    <template
                                        v-if="
                                            !currentAccount.fbinsights.audience_country.length &&
                                            !currentAccount.fbinsights.audience_city.length &&
                                            !currentAccount.fbinsights.audience_gender_age.length
                                        "
                                    >
                                        <div class="card card-custom gutter-b">
                                            <div class="card-header">
                                                <div class="card-title">
                                                    <span class="card-icon">
                                                        <i
                                                            class="flaticon2-chat-1 text-primary"
                                                        ></i>
                                                    </span>
                                                    <h3 class="card-label">
                                                        No insights available for the moment
                                                    </h3>
                                                </div>
                                            </div>
                                            <div class="card-body">
                                                <p class="lead">
                                                    There are currently no insights available for
                                                    this category. Please try again later.
                                                </p>
                                            </div>
                                        </div>
                                    </template>
                                    <!-- NOTE - Audience Country -->
                                    <template
                                        v-if="currentAccount.fbinsights.audience_country.length"
                                    >
                                        <!--begin::Card-->
                                        <div class="card card-custom gutter-b">
                                            <!--begin::Header-->
                                            <div class="card-header py-3">
                                                <div
                                                    class="card-title align-items-start flex-column"
                                                >
                                                    <h3
                                                        class="card-label font-weight-bolder text-dark"
                                                    >
                                                        Audience Country
                                                    </h3>
                                                    <span
                                                        class="text-muted font-weight-bold font-size-sm mt-1"
                                                        >Main countries that make up your
                                                        followers</span
                                                    >
                                                </div>
                                            </div>
                                            <!--end::Header-->
                                            <!-- Body -->
                                            <div class="card-body">
                                                <apexchart
                                                    :options="
                                                        currentAccount.fbinsights_charts
                                                            .audienceCountryOptions
                                                    "
                                                    :series="
                                                        currentAccount.fbinsights_charts
                                                            .audienceCountrySeries
                                                    "
                                                    type="bar"
                                                    height="300px"
                                                ></apexchart>
                                            </div>
                                            <!-- End Body -->
                                        </div>
                                    </template>
                                    <!-- END -->
                                    <!-- NOTE - Audience City -->
                                    <template v-if="currentAccount.fbinsights.audience_city.length">
                                        <!--begin::Card-->
                                        <div class="card card-custom gutter-b">
                                            <!--begin::Header-->
                                            <div class="card-header py-3">
                                                <div
                                                    class="card-title align-items-start flex-column"
                                                >
                                                    <h3
                                                        class="card-label font-weight-bolder text-dark"
                                                    >
                                                        Audience Cities
                                                    </h3>
                                                    <span
                                                        class="text-muted font-weight-bold font-size-sm mt-1"
                                                        >Main cities that make up your
                                                        followers</span
                                                    >
                                                </div>
                                            </div>
                                            <!--end::Header-->
                                            <!-- Body -->
                                            <div class="card-body">
                                                <apexchart
                                                    :options="
                                                        currentAccount.fbinsights_charts
                                                            .audienceCityOptions
                                                    "
                                                    :series="
                                                        currentAccount.fbinsights_charts
                                                            .audienceCitySeries
                                                    "
                                                    type="bar"
                                                    height="300px"
                                                ></apexchart>
                                            </div>
                                            <!-- End Body -->
                                        </div>
                                    </template>
                                    <!-- END -->
                                    <!-- NOTE - Audience Gender & Age -->
                                    <template
                                        v-if="currentAccount.fbinsights.audience_gender_age.length"
                                    >
                                        <!--begin::Card-->
                                        <div class="card card-custom gutter-b">
                                            <!--begin::Header-->
                                            <div class="card-header py-3">
                                                <div
                                                    class="card-title align-items-start flex-column"
                                                >
                                                    <h3
                                                        class="card-label font-weight-bolder text-dark"
                                                    >
                                                        Gender &amp; Age Split
                                                    </h3>
                                                    <span
                                                        class="text-muted font-weight-bold font-size-sm mt-1"
                                                        >Follower demographics</span
                                                    >
                                                </div>
                                            </div>
                                            <!--end::Header-->
                                            <!-- Body -->
                                            <div class="card-body">
                                                <apexchart
                                                    :options="
                                                        currentAccount.fbinsights_charts
                                                            .audienceGenderOptions
                                                    "
                                                    :series="
                                                        currentAccount.fbinsights_charts
                                                            .audienceGenderSeries
                                                    "
                                                    type="bar"
                                                    height="300px"
                                                ></apexchart>
                                            </div>
                                            <!-- End Body -->
                                        </div>
                                    </template>
                                    <!-- END -->
                                    <template
                                        v-if="currentAccount.fbinsights.audience_locale.length"
                                    >
                                        <!--begin::Card-->
                                        <div class="card card-custom gutter-b">
                                            <!--begin::Header-->
                                            <div class="card-header py-3">
                                                <div
                                                    class="card-title align-items-start flex-column"
                                                >
                                                    <h3
                                                        class="card-label font-weight-bolder text-dark"
                                                    >
                                                        Audience Languages
                                                    </h3>
                                                    <span
                                                        class="text-muted font-weight-bold font-size-sm mt-1"
                                                        >Main languages spoken by your
                                                        audience</span
                                                    >
                                                </div>
                                            </div>
                                            <!--end::Header-->
                                            <!-- Body -->
                                            <div class="card-body">
                                                <apexchart
                                                    type="donut"
                                                    :options="
                                                        currentAccount.fbinsights_charts
                                                            .audienceLocaleOptions
                                                    "
                                                    :series="
                                                        currentAccount.fbinsights_charts
                                                            .audienceLocaleSeries
                                                    "
                                                    height="350px"
                                                ></apexchart>
                                            </div>
                                            <!-- End Body -->
                                        </div>
                                    </template>
                                    <!-- END -->
                                </div>

                                <!-- NOTE - Growth Panel -->
                                <!-- <div v-if="currentInsightsPanel == 'posts'" key="3">
                                    <template v-if="currentAccount.fbinsights.posts.length">
                                        <div class="card card-custom gutter-b">
                                            <div class="card-header py-3">
                                                <div
                                                    class="card-title align-items-start flex-column"
                                                >
                                                    <h3
                                                        class="card-label font-weight-bolder text-dark"
                                                    >
                                                        Post Reach &amp; Impressions
                                                    </h3>
                                                    <span
                                                        class="text-muted font-weight-bold font-size-sm mt-1"
                                                        >All posts submitted to Instagram</span
                                                    >
                                                </div>
                                            </div>
                                            <div class="card-body">
                                                <pre>
                                            {{ currentAccount.fbinsights.posts }}
                                        </pre
                                                >
                                            </div>
                                        </div>
                                    </template>
                                    <template v-else>
                                        <div class="card card-custom gutter-b">
                                            <div class="card-header">
                                                <div class="card-title">
                                                    <span class="card-icon">
                                                        <i
                                                            class="flaticon2-chat-1 text-primary"
                                                        ></i>
                                                    </span>
                                                    <h3 class="card-label">
                                                        No insights available for the moment
                                                    </h3>
                                                </div>
                                            </div>
                                            <div class="card-body">
                                                <p class="lead">
                                                    There are currently no insights available for
                                                    this category. Please try again later.
                                                </p>
                                            </div>
                                        </div>
                                    </template>
                                </div> -->
                                <!--end:: Card -->
                            </transition-group>
                            <!-- End:: Groups -->
                        </template>
                        <!--begin::Aside-->
                        <template v-if="isEmptyObject(currentAccount.fbinsights)">
                            <template v-if="currentAccount.user.globals.has_facebook">
                                <div class="col-xl-12">
                                    <div class="card card-custom">
                                        <div class="card-body">
                                            <p class="lead">
                                                Hashtastic is still gathering insights for this
                                                account. If they don't appear shortly, please
                                                contact an Admin via the
                                                <router-link to="/support">Support</router-link>
                                                page.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </template>
                            <template v-else>
                                <div class="col-xl-12">
                                    <div class="card card-custom">
                                        <div class="card-body">
                                            <p class="lead">
                                                There are no insights for this account yet. In order
                                                to activate insights you need to link your Facebook
                                                account to Hashtastic.
                                            </p>
                                            <b-button
                                                class="btn btn-primary btn-lg"
                                                @click="openAddAccountModal()"
                                                >Link Facebook Account</b-button
                                            >
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </template>
                    </template>
                </div>
                <!-- NOTE - End insights tabs -->

                <!-- NOTE - Insights tab -->
                <div v-if="currentTab == 'growth'" key="tab_growth" class="row">
                    <!-- NOTE - Growth -->
                    <template v-if="currentAccount.growthIsLoading">
                        <PagePreloader>growth statistics</PagePreloader>
                    </template>
                    <template v-if="currentAccount.growthinsights.growth.length">
                        <div class="col-xl-12">
                            <div class="card card-custom gutter-b">
                                <!--begin::Header-->
                                <div class="card-header py-3">
                                    <div class="card-title align-items-start flex-column">
                                        <h3 class="card-label font-weight-bolder text-dark">
                                            Follower Growth
                                        </h3>
                                        <span class="text-muted font-weight-bold font-size-sm mt-1"
                                            >Since adding to Hashtastic</span
                                        >
                                    </div>
                                </div>
                                <!--end::Header-->
                                <!-- Body -->
                                <div class="card-body">
                                    <apexchart
                                        :options="
                                            currentAccount.growthinsights_charts
                                                .growthFollowerOptions
                                        "
                                        :series="
                                            currentAccount.growthinsights_charts
                                                .growthFollowerSeries
                                        "
                                        type="area"
                                    ></apexchart>
                                </div>
                                <!-- End Body -->
                            </div>
                        </div>
                        <!--begin::Card-->
                    </template>
                </div>
                <!-- NOTE - End insights tabs -->
            </template>
        </template>

        <!-- SECTION - Add account wizard -->
        <template v-if="currentSection == 'wizard'">
            <div class="card card-custom">
                <div class="card-body p-0">
                    <!--begin::Wizard 6-->
                    <div
                        class="wizard wizard-6 d-flex flex-column flex-lg-row flex-column-fluid"
                        id="kt_wizard"
                    >
                        <!--begin::Container-->
                        <div
                            class="wizard-content d-flex flex-column mx-auto py-10 py-lg-20 w-100 w-md-700px"
                        >
                            <!--begin::Nav-->
                            <div class="d-flex flex-column-auto flex-column px-10">
                                <!--begin: Wizard Nav-->
                                <div
                                    class="wizard-nav pb-lg-10 pb-3 d-flex flex-column align-items-center align-items-md-start"
                                >
                                    <!--begin::Wizard Steps-->
                                    <div class="wizard-steps d-flex flex-column flex-md-row">
                                        <!--NOTE - begin::Wizard Step 1 Nav-->
                                        <div
                                            class="wizard-step flex-grow-1 flex-basis-0"
                                            data-wizard-type="step"
                                            :data-wizard-state="wizardIsCurrentStep(1)"
                                        >
                                            <div class="wizard-wrapper pr-lg-7 pr-5">
                                                <div class="wizard-icon">
                                                    <i class="wizard-check ki ki-check"></i>
                                                    <span class="wizard-number">1</span>
                                                </div>
                                                <div class="wizard-label mr-3">
                                                    <h3 class="wizard-title">Account</h3>
                                                    <div class="wizard-desc">Account details</div>
                                                </div>
                                                <span class="svg-icon">
                                                    <inline-svg
                                                        src="/media/svg/icons/Navigation/Right-2.svg"
                                                    ></inline-svg>
                                                </span>
                                            </div>
                                        </div>
                                        <!--end::Wizard Step 1 Nav-->

                                        <!--NOTE - begin::Wizard Step 2 Nav-->
                                        <div
                                            class="wizard-step flex-grow-1 flex-basis-0"
                                            data-wizard-type="step"
                                            :data-wizard-state="wizardIsCurrentStep(3)"
                                        >
                                            <div class="wizard-wrapper pr-lg-7 pr-5">
                                                <div class="wizard-icon">
                                                    <i class="wizard-check ki ki-check"></i>
                                                    <span class="wizard-number">2</span>
                                                </div>
                                                <div class="wizard-label mr-3">
                                                    <h3 class="wizard-title">Options</h3>
                                                    <div class="wizard-desc">
                                                        Monitoring options
                                                    </div>
                                                </div>
                                                <span class="svg-icon">
                                                    <inline-svg
                                                        src="/media/svg/icons/Navigation/Right-2.svg"
                                                    ></inline-svg>
                                                </span>
                                            </div>
                                        </div>
                                        <!--end::Wizard Step 2 Nav-->

                                        <!--NOTE - begin::Wizard Step 3 Nav-->
                                        <div
                                            class="wizard-step flex-grow-1 flex-basis-0"
                                            data-wizard-type="step"
                                            :data-wizard-state="wizardIsCurrentStep(4)"
                                        >
                                            <div class="wizard-wrapper">
                                                <div class="wizard-icon">
                                                    <i class="wizard-check ki ki-check"></i>
                                                    <span class="wizard-number">3</span>
                                                </div>
                                                <div class="wizard-label">
                                                    <h3 class="wizard-title">Complete</h3>
                                                    <div class="wizard-desc">Submit form</div>
                                                </div>
                                            </div>
                                        </div>
                                        <!--end::Wizard Step 3 Nav-->
                                    </div>
                                    <!--end::Wizard Steps-->
                                </div>
                                <!--end: Wizard Nav-->
                                <!-- Error management -->
                                <CustomAlerts :errors="wizard.errors"></CustomAlerts>
                            </div>
                            <!--end::Nav-->
                            <!--begin::Form-->
                            <span class="px-10" id="kt_form">
                                <!--NOTE - begin: Wizard Step 1-->
                                <div
                                    class="pb-5"
                                    data-wizard-type="step-content"
                                    :data-wizard-state="wizardIsCurrentStep(1)"
                                >
                                    <!--begin::Title-->
                                    <div class="pb-10 pb-lg-12 text-center text-md-left">
                                        <h3 class="font-weight-bolder text-dark font-size-h2">
                                            Manage your Instagram Account
                                        </h3>
                                        <div class="text-muted font-weight-bold font-size-h4">
                                            Enter your Instagram username below to start tracking
                                            your progress and hashtag performance.
                                        </div>
                                    </div>
                                    <!--begin::Title-->
                                    <!--begin::Form Group-->
                                    <div class="form-group">
                                        <form v-on:submit.prevent="wizardNextStep()">
                                            <label class="font-size-h6 font-weight-bolder text-dark"
                                                >Username</label
                                            >
                                            <input
                                                type="text"
                                                class="form-control form-control-solid h-auto p-5 border-0 rounded-lg font-size-h6"
                                                name="ig_account"
                                                placeholder="@Username"
                                                value=""
                                                v-model="wizard.form.username"
                                            />
                                            <b-button
                                                type="submit"
                                                class="d-none"
                                                @click="wizardNextStep()"
                                                :disabled="wizard.buttonIsLoading"
                                            ></b-button>
                                        </form>
                                    </div>
                                    <!--end::Form Group-->
                                    <b-alert show variant="light"
                                        >Please note, we are currently only able to scan for
                                        Business / Creator / Professional accounts due to new
                                        restrictions imposed by Instagram. In order to use this
                                        service, please convert your account to a Business / Creator
                                        account from within the Instagram App.</b-alert
                                    >
                                </div>
                                <!--end: Wizard Step 1-->

                                <!--NOTE - begin: Wizard Step 2-->
                                <div
                                    class="pb-5"
                                    data-wizard-type="step-content"
                                    :data-wizard-state="wizardIsCurrentStep(2)"
                                >
                                    <!--begin::Title-->
                                    <div class="pt-lg-0 pt-5 pb-5 text-center text-md-left">
                                        <h3 class="font-weight-bolder text-dark font-size-h2">
                                            Confirm account
                                        </h3>
                                        <div class="text-muted font-weight-bold font-size-h4">
                                            Is this the account you would like to add to Hashtastic?
                                        </div>
                                    </div>
                                    <!--begin::Title-->
                                    <div class="row">
                                        <template v-if="wizard.account_data.user">
                                            <AccountBadgeWide :account="wizard.account_data.user">
                                            </AccountBadgeWide>
                                        </template>
                                    </div>
                                </div>
                                <!--end: Wizard Step 2-->
                                <!--NOTE - begin: Wizard Step 3-->
                                <div
                                    class="pb-5"
                                    data-wizard-type="step-content"
                                    :data-wizard-state="wizardIsCurrentStep(3)"
                                >
                                    <!--begin::Title-->
                                    <div class="pt-lg-0 pt-5 pb-15 text-center text-md-left">
                                        <h3 class="font-weight-bolder text-dark font-size-h2">
                                            Options
                                        </h3>
                                        <div class="text-muted font-weight-bold font-size-h4">
                                            What would you like to do with this account?
                                        </div>
                                    </div>
                                    <!--end::Title-->
                                    <div class="form-group">
                                        <!-- <label>Search Mode</label> -->
                                        <template v-if="!canAddCampaigns">
                                            <div
                                                class="alert alert-custom alert-notice alert-light-warning fade show gutter-b"
                                                role="alert"
                                            >
                                                <div class="alert-icon">
                                                    <i class="flaticon-warning"></i>
                                                </div>
                                                <div class="alert-text">
                                                    Unfortunately you have run out of active
                                                    Campaigns you can run. You can either stop one
                                                    of your current campaigns, or
                                                    <router-link to="/subscribe"
                                                        >upgrade your account</router-link
                                                    >
                                                    to enable more concurrent Hashtag Performance
                                                    Campaigns!
                                                </div>
                                                <div class="alert-close">
                                                    <button
                                                        type="button"
                                                        class="close"
                                                        data-dismiss="alert"
                                                        aria-label="Close"
                                                    >
                                                        <span aria-hidden="true">
                                                            <i class="ki ki-close"></i>
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        </template>
                                        <div class="row">
                                            <div class="col-lg-6">
                                                <label
                                                    class="option option-plain"
                                                    :class="{
                                                        'bg-light': !canAddCampaigns,
                                                    }"
                                                >
                                                    <span class="option-control">
                                                        <span class="radio">
                                                            <input
                                                                type="radio"
                                                                name="mode"
                                                                value="campaign"
                                                                checked="checked"
                                                                v-model="wizard.form.selectedMode"
                                                                :disabled="!canAddCampaigns"
                                                            />
                                                            <span></span>
                                                        </span>
                                                    </span>
                                                    <span class="option-label">
                                                        <span class="option-head">
                                                            <span class="option-title">
                                                                Enable Hashtastic Performance
                                                            </span>
                                                            <span class="option-focus"> </span>
                                                        </span>
                                                        <span class="option-body">
                                                            Start a Hashtag Performance Campaign on
                                                            this account to identify the best
                                                            performing hashtags on all of your
                                                            posts, as well as monitor overall growth
                                                            and performance.
                                                        </span>
                                                    </span>
                                                </label>
                                            </div>

                                            <div class="col-lg-6">
                                                <label class="option option-plain">
                                                    <span class="option-control">
                                                        <span class="radio">
                                                            <input
                                                                type="radio"
                                                                name="mode"
                                                                value="scanner"
                                                                v-model="wizard.form.selectedMode"
                                                            />
                                                            <span></span>
                                                        </span>
                                                    </span>
                                                    <span class="option-label">
                                                        <span class="option-head">
                                                            <span class="option-title">
                                                                Only monitor growth
                                                            </span>
                                                            <span class="option-focus"> </span>
                                                        </span>
                                                        <span class="option-body">
                                                            Don't scan hashtags, only monitor
                                                            overall growth and engagement.
                                                        </span>
                                                    </span>
                                                </label>
                                            </div>
                                        </div>
                                        <template v-if="wizard.form.selectedMode == 'scanner'">
                                            <div class="row">
                                                <div class="col-lg-6">
                                                    <label class="option option-plain">
                                                        <span class="option-control">
                                                            <span class="radio">
                                                                <input
                                                                    type="radio"
                                                                    name="type"
                                                                    value="personal"
                                                                    checked="checked"
                                                                    v-model="
                                                                        wizard.form
                                                                            .scannerPreference
                                                                    "
                                                                />
                                                                <span></span>
                                                            </span>
                                                        </span>
                                                        <span class="option-label">
                                                            <span class="option-head">
                                                                <span class="option-title">
                                                                    Personal Account
                                                                </span>
                                                                <span class="option-focus"> </span>
                                                            </span>
                                                            <span class="option-body">
                                                                For your personal accounts.
                                                            </span>
                                                        </span>
                                                    </label>
                                                </div>

                                                <div class="col-lg-6">
                                                    <label class="option option-plain">
                                                        <span class="option-control">
                                                            <span class="radio">
                                                                <input
                                                                    type="radio"
                                                                    name="type"
                                                                    value="competitor"
                                                                    v-model="
                                                                        wizard.form
                                                                            .scannerPreference
                                                                    "
                                                                />
                                                                <span></span>
                                                            </span>
                                                        </span>
                                                        <span class="option-label">
                                                            <span class="option-head">
                                                                <span class="option-title">
                                                                    Competitor Account
                                                                </span>
                                                                <span class="option-focus"> </span>
                                                            </span>
                                                            <span class="option-body">
                                                                To keep track of your competition.
                                                            </span>
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                                <!--end: Wizard Step 3-->
                                <!--NOTE - begin: Wizard Step 3-->
                                <!-- <div
                                    class="pb-5"
                                    data-wizard-type="step-content"
                                    :data-wizard-state="wizardIsCurrentStep(4)"
                                >
                                    <div class="pt-lg-0 pt-5 pb-15 text-center text-md-left">
                                        <h3 class="font-weight-bolder text-dark font-size-h2">
                                            Complete
                                        </h3>
                                        <div class="text-muted font-weight-bold font-size-h4">
                                            Complete Your Signup And Become A Member!
                                        </div>
                                    </div>
                                    <h4 class="font-weight-bolder mb-3">Accoun Settings:</h4>
                                    <div class="text-dark-50 font-weight-bold line-height-lg mb-8">
                                        <div>Nick Stone</div>
                                        <div>+12233434-34</div>
                                        <div>nick.stone@gmail.com</div>
                                    </div>
                                    <h4 class="font-weight-bolder mb-3">Address Details:</h4>
                                    <div class="text-dark-50 font-weight-bold line-height-lg mb-8">
                                        <div>Address Line 1</div>
                                        <div>Address Line 2</div>
                                        <div>Melbourne 3000, VIC, Australia</div>
                                    </div>
                                    <h4 class="font-weight-bolder mb-3">Support Channels:</h4>
                                    <div class="text-dark-50 font-weight-bold line-height-lg">
                                        <div>Overnight Delivery with Regular Packaging</div>
                                        <div>Preferred Morning (8:00AM - 11:00AM) Delivery</div>
                                    </div>
                                </div> -->
                                <!--end: Wizard Step 3-->
                                <!--begin: Wizard Actions-->

                                <div class="d-flex justify-content-between pt-7">
                                    <!-- NOTE - Left button containers -->
                                    <div class="mr-2">
                                        <!-- NOTE - Cancel button -->
                                        <button
                                            type="button"
                                            class="btn btn-outline-danger font-weight-bolder font-size-h6 pl-8 pr-4 py-4 my-3 mr-2"
                                            @click="openAccountSelectionSection()"
                                        >
                                            <span class="svg-icon svg-icon-md mr-2">
                                                <inline-svg
                                                    src="/media/svg/icons/Code/Backspace.svg"
                                                ></inline-svg>
                                            </span>
                                            Cancel
                                        </button>
                                        <!-- NOTE - Back button -->
                                        <button
                                            type="button"
                                            class="btn btn-light-primary font-weight-bolder font-size-h6 pr-8 pl-6 py-4 my-3 mr-3"
                                            :class="wizardShowPreviousButton()"
                                            @click="wizardPreviousStep()"
                                            :disabled="wizard.buttonIsLoading"
                                        >
                                            <span class="svg-icon svg-icon-md mr-2">
                                                <inline-svg
                                                    src="/media/svg/icons/Navigation/Left-2.svg"
                                                ></inline-svg> </span
                                            >Previous
                                        </button>
                                    </div>
                                    <!-- Containers for right side buttons -->
                                    <div>
                                        <!-- NOTE - Submit button -->
                                        <button
                                            type="button"
                                            class="btn btn-success font-weight-bolder font-size-h6 pl-8 pr-4 py-4 my-3"
                                            :class="[
                                                wizardShowSubmitButton(),
                                                wizardButtonIsLoading(),
                                            ]"
                                            @click="wizardNextStep()"
                                            :disabled="wizard.buttonIsLoading"
                                        >
                                            Submit
                                            <span class="svg-icon svg-icon-md ml-2">
                                                <inline-svg
                                                    src="/media/svg/icons/Navigation/Right-2.svg"
                                                ></inline-svg>
                                            </span>
                                        </button>
                                        <!-- NOTE - Right button button -->
                                        <button
                                            type="button"
                                            class="btn btn-primary font-weight-bolder font-size-h6 pl-8 pr-4 py-4 my-3"
                                            :class="[
                                                wizardShowNextButton(),
                                                wizardButtonIsLoading(),
                                            ]"
                                            @click="wizardNextStep()"
                                            :disabled="wizard.buttonIsLoading"
                                        >
                                            Next
                                            <span class="svg-icon svg-icon-md ml-2">
                                                <inline-svg
                                                    src="/media/svg/icons/Navigation/Right-2.svg"
                                                ></inline-svg>
                                            </span>
                                        </button>
                                    </div>
                                </div>
                                <!--end: Wizard Actions-->
                            </span>
                            <!--end::Form-->
                        </div>
                        <!--end::Container-->
                    </div>
                    <!--end::Wizard 6-->
                </div>
                <!--end::Wizard-->
            </div>
        </template>
        <!-- SECTION END - Wizard -->
    </div>
</template>

<style lang="scss" scoped>
@import "~bootstrap-vue/dist/bootstrap-vue.css";
</style>
<style scoped>
.wizard.wizard-6 .wizard-content .wizard-nav {
    padding: 0;
}
.wizard.wizard-6 .wizard-content .wizard-nav .wizard-steps {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
}
.wizard.wizard-6 .wizard-content .wizard-nav .wizard-steps .wizard-step {
    padding: 0.75rem 0;
    -webkit-transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease, -webkit-box-shadow 0.15s ease;
    margin-bottom: 1.5rem;
}
.wizard.wizard-6 .wizard-content .wizard-nav .wizard-steps .wizard-step .wizard-wrapper {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
}
.wizard.wizard-6 .wizard-content .wizard-nav .wizard-steps .wizard-step .wizard-icon {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-align: center;
    -ms-flex-align: center;
    align-items: center;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
    -webkit-transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease, -webkit-box-shadow 0.15s ease;
    min-width: 46px;
    height: 46px;
    border-radius: 12px;
    background-color: #f3f6f9;
    margin-right: 1rem;
}
.wizard.wizard-6 .wizard-content .wizard-nav .wizard-steps .wizard-step .wizard-icon .wizard-check {
    display: none;
    font-size: 1.4rem;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step
    .wizard-icon
    .wizard-number {
    font-weight: 600;
    color: #3f4254;
    font-size: 1.35rem;
}
.wizard.wizard-6 .wizard-content .wizard-nav .wizard-steps .wizard-step .wizard-label {
    display: -webkit-box;
    display: -ms-flexbox;
    display: flex;
    -webkit-box-orient: vertical;
    -webkit-box-direction: normal;
    -ms-flex-direction: column;
    flex-direction: column;
    -webkit-box-pack: center;
    -ms-flex-pack: center;
    justify-content: center;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step
    .wizard-label
    .wizard-title {
    color: #181c32;
    font-weight: 600;
    font-size: 1.24rem;
}
.wizard.wizard-6 .wizard-content .wizard-nav .wizard-steps .wizard-step .wizard-label .wizard-desc {
    color: #b5b5c3;
    font-size: 0.925rem;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"] {
    -webkit-transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease, -webkit-box-shadow 0.15s ease;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]
    .wizard-icon {
    -webkit-transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease, -webkit-box-shadow 0.15s ease;
    background-color: #c9f7f5;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]
    .wizard-icon
    .wizard-check {
    color: #1bc5bd;
    display: none;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]
    .wizard-icon
    .wizard-number {
    color: #1bc5bd;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]
    .wizard-label
    .wizard-title {
    color: #181c32;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]
    .wizard-label
    .wizard-desc {
    color: #b5b5c3;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]:last-child
    .wizard-icon,
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="done"]
    .wizard-icon {
    -webkit-transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        -webkit-box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease;
    transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease,
        box-shadow 0.15s ease, -webkit-box-shadow 0.15s ease;
    background-color: #c9f7f5;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]:last-child
    .wizard-icon
    .wizard-check,
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="done"]
    .wizard-icon
    .wizard-check {
    color: #1bc5bd;
    display: inline-block;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]:last-child
    .wizard-icon
    .wizard-number,
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="done"]
    .wizard-icon
    .wizard-number {
    display: none;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]:last-child
    .wizard-label
    .wizard-title,
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="done"]
    .wizard-label
    .wizard-title {
    color: #b5b5c3;
}
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="current"]:last-child
    .wizard-label
    .wizard-desc,
.wizard.wizard-6
    .wizard-content
    .wizard-nav
    .wizard-steps
    .wizard-step[data-wizard-state="done"]
    .wizard-label
    .wizard-desc {
    color: #d1d3e0;
}

@media (max-width: 767.98px) {
    .wizard.wizard-6 .wizard-content .wizard-nav .wizard-steps .wizard-step {
        width: 100%;
    }
    .wizard.wizard-6
        .wizard-content
        .wizard-nav
        .wizard-steps
        .wizard-step
        .wizard-wrapper
        .svg-icon {
        display: none;
    }
}
.account-bio {
    white-space: pre-line;
    width: 100%;
}
.hover-pointer {
    cursor: pointer;
}
.custom-v-dropdown {
    &.dropdown-toggle {
        padding: 0;
        &:hover {
            text-decoration: none;
        }

        &.dropdown-toggle-no-caret {
            &:after {
                content: none;
            }
        }
    }

    &.dropdown-menu {
        margin: 0;
        padding: 0;
        outline: none;
        .b-dropdown-text {
            padding: 0;
        }
    }
}
</style>

<script>
// Defaults
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import { mapGetters, mapActions } from "vuex";
import axios from "axios";

// Components
import PagePreloader from "@/view/content/Custom/PagePreloader.vue";
import CustomAlerts from "@/view/content/Custom/CustomAlerts.vue";
import AccountBadgeWide from "@/view/content/Custom/AccountBadgeWide.vue";
import AccountDashboardPost from "@/view/content/Custom/AccountDashboardPost.vue";
import InfoBox from "@/view/content/Custom/InfoBox.vue";
import EnterpriseLogoDisplay from "@/view/content/Custom/EnterpriseLogoDisplay.vue";

// Buttons
import buttonAddToExistingHashtagSet from "@/view/content/Custom/buttonAddToExistingHashtagSet.vue";
import buttonCreateNewHashtagSet from "@/view/content/Custom/buttonCreateNewHashtagSet.vue";
import buttonCopyToClipboard from "@/view/content/Custom/buttonCopyToClipboard.vue";

// Mixins
import axiosErrorManagement from "@/mixins/axiosErrorManagement";
import stringManagement from "@/mixins/stringManagement";
import hashtagTableManagement from "@/mixins/hashtagTableManagement";

export default {
    name: "AccountDashboard",
    mixins: [axiosErrorManagement, stringManagement, hashtagTableManagement],
    components: {
        PagePreloader,
        CustomAlerts,

        AccountBadgeWide,
        AccountDashboardPost,

        buttonAddToExistingHashtagSet,
        buttonCreateNewHashtagSet,
        buttonCopyToClipboard,

        InfoBox,
        EnterpriseLogoDisplay,
    },
    data() {
        return {
            // Page management
            currentSection: "starter",
            currentTab: "posts",
            currentInsightsPanel: "growth",

            // Search
            searchAccount: "",

            // Campaign Results Summary Table
            summaryCurrentPage: 1,
            summaryTabIndex: 0,

            // Selected hashtags
            selectedHashtags: [],
            allSelectedHashtags: [],

            // Error management
            accountErrors: [],

            // Account management
            currentAccount: {
                // User
                user: [],

                // Posts
                posts: [],
                postsCurrentPage: 1,
                postsIsLoading: false,

                // FB Insights
                fbinsights: [],
                fbinsightsIsLoading: false,
                fbinsights_charts: {
                    audienceCountryOptions: {},
                    audienceCountrySeries: [],

                    audienceCityOptions: {},
                    audienceCitySeries: [],

                    audienceGenderOptions: {},
                    audienceGenderSeries: [],

                    audienceLocaleOptions: {},
                    audienceLocaleSeries: [],

                    reachImpressionsOptions: {},
                    reachImpressionsSeries: [],

                    reachImpressionsByMonthOptions: {},
                    reachImpressionsByMonthSeries: [],

                    // Charts
                    campaignResultsChartSeries: [],
                    campaignResultsChartLabels: [],
                    campaignResultsChartOptions: {},
                },
                growthinsights: [],
                growthIsLoading: false,
                growthinsights_charts: {
                    growthFollowerOptions: {},
                    growthFollowerSeries: [],
                },

                // Campaign
                campaignResults: [],
                campaignResultsIsLoading: false,
            },

            // Wizard
            wizard: {
                // Step management
                currentStep: 1,
                totalSteps: 3,

                // Loaders
                buttonIsLoading: false,

                // Form
                form: {
                    username: "",
                    selectedMode: "campaign",
                    scannerPreference: "personal",
                },

                // Account data
                account_data: {},

                // Errors
                errors: [],
            },

            // Modals
            modals: {
                facebook: {
                    errors: [],
                    isLoading: false,
                },
                selectAccount: {
                    isLoading: false,
                    errors: [],
                    type: "all",
                    subtype: "all",
                },
                campaign: {
                    errors: [],
                    isLoading: false,
                },
                deletecampaign: {
                    errors: [],
                    isLoading: false,
                },
                deleteaccount: {
                    errors: [],
                    isLoading: false,
                },
            },

            // Dashboard
            dashboardIsLoading: true,
            dashboard: {
                accounts: [],
                globals: [],
            },

            // Graphs
            fieldsResultsSummary: [
                {
                    key: "selected",
                    label: "",
                },
                {
                    key: "hashtag",
                    sortable: true,
                    tdClass: "d-none",
                    thClass: "d-none",
                },
                {
                    key: "hashtagsBadged",
                    sortable: true,
                    label: "Hashtags",
                },
                {
                    key: "usedcount",
                    sortable: true,
                    label: "Number of times used",
                },
                {
                    key: "top9count",
                    sortable: true,
                    label: "Number of times you ranked in top9",
                },
                {
                    key: "performanceTop9",
                    sortable: true,
                    label: "Top9 Performance",
                    sortByFormatted: true,
                    formatter: (value, key, item) => {
                        if (item.top9count < 1) {
                            return "0%";
                        }
                        return Math.round((item.top9count / item.usedcount) * 100, 2) + "%";
                    },
                },
                {
                    key: "rankcount",
                    sortable: true,
                    label: "Number of times you ranked in top100",
                },
                {
                    key: "performance100",
                    sortable: true,
                    label: "Top100 Performance",
                    sortByFormatted: true,
                    formatter: (value, key, item) => {
                        if (item.rankcount < 1) {
                            return "0%";
                        }
                        return Math.round((item.rankcount / item.usedcount) * 100, 2) + "%";
                    },
                },
                {
                    key: "lowestrank",
                    sortable: true,
                    label: "Best rank",
                    formatter: (value, key, item) => {
                        return this.makeOrdinal(value);
                    },
                },
                {
                    key: "highestrank",
                    sortable: true,
                    label: "Worst rank",
                    formatter: (value, key, item) => {
                        return this.makeOrdinal(value);
                    },
                },
            ],
        };
    },

    methods: {
        isEmptyObject(obj) {
            for (var prop in obj) {
                if (obj.hasOwnProperty(prop)) return false;
            }
            return true;
        },
        openAccountSelectionSection() {
            this.currentSection = "starter";
        },
        openWizardSection() {
            // Set to wizard
            this.currentSection = "wizard";

            // Reset steps
            this.wizard.currentStep = 1;
        },
        resetCurrentAccount() {
            // Selected hashtags
            this.selectedHashtags = [];

            // User
            this.currentAccount = {
                // User
                user: [],

                // Posts
                posts: [],
                postsCurrentPage: 1,
                postsIsLoading: false,

                // Selected hashtags
                selectedHashtags: [],
                allSelectedHashtags: [],

                // FB Insights
                fbinsights: [],
                fbinsightsIsLoading: false,
                fbinsights_charts: {
                    audienceCountryOptions: {},
                    audienceCountrySeries: [],

                    audienceCityOptions: {},
                    audienceCitySeries: [],

                    audienceGenderOptions: {},
                    audienceGenderSeries: [],

                    audienceLocaleOptions: {},
                    audienceLocaleSeries: [],

                    reachImpressionsOptions: {},
                    reachImpressionsSeries: [],

                    reachImpressionsByMonthOptions: {},
                    reachImpressionsByMonthSeries: [],

                    // Charts
                    campaignResultsChartSeries: [],
                    campaignResultsChartLabels: [],
                    campaignResultsChartOptions: {},
                },

                // Growth
                growthinsights: [],
                growthIsLoading: false,
                growthinsights_charts: {
                    growthFollowerOptions: {},
                    growthFollowerSeries: [],
                },
            };

            this.summaryCurrentPage = 1;
            this.summaryTabIndex = 0;
        },
        loadDashboard() {
            axios
                .get("accounts/dashboard")
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Set stuff
                    this.dashboard = data.data;
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close loader
                    this.dashboardIsLoading = false;
                });
        },
        handleAccountSearch(username) {
            if (this.searchAccount) {
                if (username.includes(this.searchAccount)) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return true;
            }
        },
        // NOTE - Wizard methods
        // NOTE - Step management
        wizardIncreaseStep() {
            if (this.wizard.currentStep < this.wizard.totalSteps) {
                this.wizard.currentStep = this.wizard.currentStep + 1;
            }
        },
        wizardDecreaseStep() {
            if (this.wizard.currentStep > 1) {
                this.wizard.currentStep = this.wizard.currentStep - 1;
            }
        },
        wizardIsCurrentStep(step) {
            if (this.wizard.currentStep == step) {
                return "current";
            } else if (step < this.wizard.currentStep) {
                return "done";
            } else {
                return "";
            }
        },
        // NOTE - Button management
        wizardShowNextButton() {
            if (this.wizard.currentStep >= this.wizard.totalSteps) {
                return "d-none";
            }
        },
        wizardShowPreviousButton() {
            if (this.wizard.currentStep < 2) {
                return "d-none";
            }
        },
        wizardShowSubmitButton() {
            if (this.wizard.currentStep < this.wizard.totalSteps) {
                return "d-none";
            }
        },
        wizardButtonIsLoading() {
            if (this.wizard.buttonIsLoading) {
                return "pl-12 spinner spinner-white spinner-left";
            }
        },
        postsButtonLoading() {
            if (this.currentAccount.postsIsLoading) {
                return "pl-12 spinner spinner-success spinner-center disabled";
            }
        },
        spinnerLoaderClass() {
            return "pl-12 spinner spinner-white spinner-left";
        },
        wizardStartButtonLoading() {
            this.wizard.buttonIsLoading = true;
        },
        wizardStopButtonLoading() {
            this.wizard.buttonIsLoading = false;
        },
        wizardResetErrors() {
            this.wizard.errors = [];
        },
        // NOTE - Handle form functionality
        wizardNextStep() {
            // Remove all errors
            this.wizardResetErrors();

            // Handle the step we're on
            // CASE - Step 1, getting the username
            if (this.wizard.currentStep == 1) {
                // Start loading
                this.wizardStartButtonLoading();

                // Submit form
                axios
                    .post("accounts/get", {
                        account: this.wizard.form.username,
                    })
                    .then((response) => {
                        // Prettify
                        var data = response.data;

                        // Check if we've got a response
                        if (data.status == "OK") {
                            // Set the data
                            this.wizard.account_data = data.data;

                            // Go to next step
                            this.wizardIncreaseStep();
                        }

                        // Handle errors
                        if (data.messages.length > 0) {
                            data.messages.forEach((message) => {
                                this.wizard.errors.push({
                                    type: message.type,
                                    message: message.message,
                                });
                            });
                        }
                    })
                    .catch((error) => {
                        this.wizard.errors.push(this.handleAxiosErrors(error));
                    })
                    .finally(() => {
                        this.wizardStopButtonLoading();
                    });

                // CASE - Step 2, confirmation of the username
            } else if (this.wizard.currentStep == 2) {
                // Simply go to the next step
                this.wizardIncreaseStep();

                // Check if we can add campaign, if not, force to "competitor" only
                if (!this.canAddCampaigns) {
                    this.wizard.form.selectedMode = "scanner";
                }
                // CASE - This is where you chose what you would like to do, adding as Hashtag Performance Campaign or simply adding as Personal / Competitor account
            } else if (this.wizard.currentStep == 3) {
                this.wizardStartButtonLoading();

                // CASE 1 - Hashtag Performance Campaign, run a check
                if (this.wizard.form.selectedMode == "campaign") {
                    // Perform post
                    axios
                        .post("campaigns/new", {
                            account: this.wizard.form.username,
                        })
                        .then((response) => {
                            // Make pretty
                            var data = response.data;

                            // Handle response
                            if (data.status == "OK") {
                                // Everything is OK, we should redirect to the page of the campaign now
                                this.loadAccount(data.data);

                                // Increase the counter for active campaigns
                                this.dashboard.globals.campaigns_current++;

                                // Re-load the dashboard
                                this.loadDashboard();
                            }

                            // Handle errors
                            if (data.messages.length > 0) {
                                data.messages.forEach((item) => {
                                    this.wizard.errors.push({
                                        type: item.type,
                                        message: item.message,
                                    });
                                });
                            }
                        })
                        .catch((error) => {
                            this.wizard.errors.push(this.handleAxiosErrors(error));
                        })
                        .finally(() => {
                            // Turn preloader off
                            this.wizardStopButtonLoading();
                        });
                }

                // CASE 2 - Only monitoring growth
                if (this.wizard.form.selectedMode == "scanner") {
                    axios
                        .post("accounts/new", {
                            account: this.wizard.form.username,
                            type: this.wizard.form.scannerPreference,
                        })
                        .then((response) => {
                            // Prettify
                            var data = response.data;

                            // Handle response
                            if (data.status == "OK") {
                                // Everything is OK, we should redirect to the page of the campaign now
                                this.loadAccount(data.data);

                                // Increase the counter for active campaigns
                                this.dashboard.globals.campaigns_current++;

                                // Re-load the dashboard
                                this.loadDashboard();
                            }

                            // Do error handling
                            if (data.messages.length > 0) {
                                data.messages.forEach((message) => {
                                    this.wizard.errors.push({
                                        type: message.type,
                                        message: message.message,
                                    });
                                });
                            }
                        })
                        .catch((error) => {
                            this.wizard.errors.push(this.handleAxiosErrors(error));
                        })
                        .finally(() => {
                            // Turn preloader off
                            this.wizardStopButtonLoading();
                        });
                }

                // this.wizard.form.selectedMode
                // this.wizard.form.scannerPreference
            }
        },
        wizardPreviousStep() {
            // Do something?
            this.wizardDecreaseStep();
        },

        /**
         * NOTE - Open select account modal
         */
        openSelectAccountModal() {
            // Open modal
            this.$refs["select-account-modal"].show();
        },
        closeSelectAccountModal() {
            // Open modal
            this.$refs["select-account-modal"].hide();
        },
        buttonVariant(variant) {
            if (this.modals.selectAccount.type == variant) {
                return "success";
            }
            return "outline-success";
        },
        setSelectAccountType(type) {
            this.modals.selectAccount.type = type;
        },
        buttonSubVariant(variant) {
            if (this.modals.selectAccount.subtype == variant) {
                return "primary";
            }
            return "outline-primary";
        },
        setSelectAccountSubType(type) {
            this.modals.selectAccount.subtype = type;
        },
        modalSelectAccountDisplay(type, subtype) {
            // Scenario 1 - Display all
            if (this.modals.selectAccount.type == "all") {
                return true;
                // Scenario 2 - Campaigns
            } else if (this.modals.selectAccount.type == "campaign") {
                if (type == "campaign") {
                    return true;
                }
                return false;
                // Scenario 3 - Tracked accounts
            } else if (this.modals.selectAccount.type == "scanner") {
                if (type == "scanner") {
                    // Scenario 3.1 - Subtype is all
                    if (this.modals.selectAccount.subtype == "all") {
                        return true;
                    } else {
                        // Scenario 3.2 - Selected subtype
                        if (this.modals.selectAccount.subtype == subtype) {
                            return true;
                        }
                        return false;
                    }
                } else {
                    return false;
                }
            }
        },
        /**
         * NOTE - Add new account
         */
        openAddAccountModal() {
            // Reset errors
            this.modals.facebook.errors = [];
            this.modals.facebook.isLoading = false;

            // Open modal
            this.$refs["add-account-modal"].show();
        },
        addAccount() {
            // Prevent double submission
            if (this.modals.facebook.isLoading == true) {
                return;
            }

            // Set loading
            this.modals.facebook.isLoading = true;

            // Reset errors
            this.modals.facebook.errors = [];

            // Perform the thing
            axios
                .get("accountinsights/add")
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "OK") {
                        // window.location.href = data.data;
                        // this.$refs["add-account-modal"].hide();
                        var win = window.open(data.data, "Hashtastic Facebook Login");
                        var timer = setInterval(() => {
                            if (win.closed) {
                                // Stop the timer
                                clearInterval(timer);

                                // Run a check to see if we have everything we need
                                axios
                                    .get("accountinsights/checkrecentcallback")
                                    .then((response) => {
                                        // Make pretty
                                        var data = response.data;

                                        // Check status
                                        if (data.status == "OK") {
                                            // Hide the modal
                                            this.$refs["add-account-modal"].hide();

                                            this.loadAccount({
                                                accountid: this.currentAccount.user.userid,
                                            });
                                        } else {
                                            // Check if we have a var that was transmitted from the previous page
                                            if (typeof win.hashtastic_message != "undefined") {
                                                // Make pretty
                                                var message = win.hashtastic_message;

                                                // Push the error
                                                if (message == "unknown_error") {
                                                    this.modals.facebook.errors.push({
                                                        type: "danger",
                                                        message:
                                                            "There was an unknown error processing your request. Please try again. If the issue persists, please contact an Admin ASAP so we can look into it for you.",
                                                    });
                                                }

                                                if (message == "missing_scope") {
                                                    this.modals.facebook.errors.push({
                                                        type: "danger",
                                                        message:
                                                            "In order to obtain insights from your Instagram accounts you need to check all the boxes when connecting Hashtastic to your Facebook account. Please try adding the account again. If the issue persists, please contact an Admin.",
                                                    });
                                                }
                                                if (message == "missing_pages") {
                                                    this.modals.facebook.errors.push({
                                                        type: "danger",
                                                        message:
                                                            "No Facebook Pages were selected while linking Hashtastic to Facebook. Please try adding the accounts again. If the issue persists, please contact an Admin.",
                                                    });
                                                }
                                                if (message == "no_ig_account") {
                                                    this.modals.facebook.errors.push({
                                                        type: "danger",
                                                        message:
                                                            "It looks like your Instagram account isn't connected to a Facebook page. Please make sure your Instagram account is connected to a Facebook account via the Instagram app, and try again.",
                                                    });
                                                }
                                                if (message == "no_accounts") {
                                                    this.modals.facebook.errors.push({
                                                        type: "warning",
                                                        message:
                                                            "No accounts were selected to be added to the tracker.",
                                                    });
                                                }
                                                if (message == "not_logged_in") {
                                                    this.modals.facebook.errors.push({
                                                        type: "warning",
                                                        message:
                                                            "It looks like you took too long to go through the Facebook login, please try again. If the issue persists, please contact an Admin.",
                                                    });
                                                }
                                                if (message == "authcode_already_used") {
                                                    this.modals.facebook.errors.push({
                                                        type: "danger",
                                                        message:
                                                            "The authorisation code provided by Facebook has already been used. Please try adding your accounts again. If the issue persists, please contact an Admin.",
                                                    });
                                                }
                                                if (message == "accesstoken_error") {
                                                    this.modals.facebook.errors.push({
                                                        type: "danger",
                                                        message:
                                                            "There was an error retrieving an access token from Facebook. Please try adding your accounts again. If the issue persists, please contact an Admin.",
                                                    });
                                                }
                                                if (message == "accesstoken_denied") {
                                                    this.modals.facebook.errors.push({
                                                        type: "danger",
                                                        message:
                                                            "There was an error retrieving an access token from Facebook. Please try adding your accounts again. If the issue persists, please contact an Admin.",
                                                    });
                                                }
                                            }

                                            this.modals.facebook.errors.push({
                                                type: "warning",
                                                message:
                                                    "It looks like you closed the Facebook Login without ticking all the boxes or filling all the necessary details, please try again. If the issue persists and you are unable to add your account, please contact an Admin asap to resolve the issue.",
                                            });
                                        }
                                    })
                                    .catch((error) => {
                                        this.modals.facebook.errors.push(
                                            this.handleAxiosErrors(error)
                                        );
                                    })
                                    .finally(() => {
                                        // Close preloader
                                        this.modals.facebook.isLoading = false;
                                    });
                            }
                        }, 500);
                    }

                    // Do error handling
                    if (data.messages.length > 0) {
                        data.messages.forEach((message) => {
                            this.modals.facebook.errors.push({
                                type: message.type,
                                message: message.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.modals.facebook.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close preloader
                    // this.modals.facebook.isLoading = false;
                });
        },
        /**
         * NOTE - Load account
         */
        loadAccount(account) {
            // Reset everything
            this.errors = [];
            this.currentAccount.user = [];
            this.resetCurrentAccount();

            // Set loader
            this.modals.selectAccount.isLoading = true;

            // Switch the page
            this.currentSection = "account";

            // Perform request
            axios
                .get("accounts/dashboard/account/" + account.accountid)
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check
                    if (data.status == "OK") {
                        // Set the data
                        this.currentAccount.user = data.data;

                        // Get posts for page 1
                        this.currentAccount.postsCurrentPage = 1;
                        this.loadPosts(account.accountid, 1);

                        // Get Facebook Insights
                        this.loadFBInsights(account.accountid);

                        // Get growth
                        this.loadGrowth(account.accountid);

                        // Get Campaign results
                        this.loadCampaign(account.accountid);

                        // Reset the tabs
                        this.switchTab("posts");

                        // Switch the page
                        this.currentSection = "account";

                        // Close the modal
                        // this.closeSelectAccountModal();
                    }

                    // Check errors
                    if (data.errors.length > 0) {
                        data.errors.forEach((message) => {
                            this.accountErrors.push({
                                type: message.type,
                                message: message.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.modals.selectAccount.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close preloader
                    // this.pageIsLoaded = true;
                    // this.displayAccounts = true;
                    this.modals.selectAccount.isLoading = false;
                });
        },
        loadPosts(accountid, page) {
            // Reset all
            this.currentAccount.postsIsLoading = true;

            // Perform the next axios request
            axios
                .get("accounts/dashboard/account/" + accountid + "/posts?page=" + page)
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Set the page number
                    this.currentAccount.postsCurrentPage = data.data.current_page;

                    // Check what page we are on
                    if (page == 1) {
                        this.currentAccount.posts = data;
                    } else {
                        data.data.data.forEach((currentItem) => {
                            this.currentAccount.posts.data.data.push(currentItem);
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Reset post loader
                    this.currentAccount.postsIsLoading = false;
                });
        },
        loadFBInsights(accountid) {
            // Reset all
            this.currentAccount.fbinsightsIsLoading = true;

            // Perform the next axios request
            axios
                .get("accounts/dashboard/account/" + accountid + "/fbinsights")
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "FAIL") {
                        // Do nothing
                    }
                    if (data.status == "OK") {
                        // Check what page we are on
                        // TODO - Handle case where we don't have insights for that user
                        this.currentAccount.fbinsights = data.data.insights;

                        if (typeof this.currentAccount.fbinsights != "undefined") {
                            // NOTE - Audience Country Chart
                            var tempData = [];
                            var tempLabels = [];
                            if (this.currentAccount.fbinsights.audience_country.length > 0) {
                                this.currentAccount.fbinsights.audience_country.forEach(
                                    (currentItem) => {
                                        tempData.push(currentItem.value);
                                        tempLabels.push(currentItem.insight_sub);
                                    }
                                );
                                this.currentAccount.fbinsights_charts.audienceCountrySeries = [
                                    {
                                        name: "Followers",
                                        data: tempData,
                                    },
                                ];
                                this.currentAccount.fbinsights_charts.audienceCountryOptions = {
                                    chart: {
                                        type: "bar",
                                        height: 350,
                                    },
                                    title: {
                                        text: "Audience split by country",
                                    },
                                    plotOptions: {
                                        bar: {
                                            borderRadius: 4,
                                            horizontal: true,
                                        },
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    labels: tempLabels,
                                    // tooltip: {
                                    //     enabled: true,
                                    //     custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                                    //         let total = 0;
                                    //         for (let x of series) {
                                    //             total += x;
                                    //         }
                                    //         let selected = series[seriesIndex];
                                    //         return (
                                    //             w.config.labels[seriesIndex] +
                                    //             ": " +
                                    //             selected +
                                    //             "(" +
                                    //             ((selected / total) * 100).toFixed(2) +
                                    //             "%)"
                                    //         );
                                    //     },
                                    // },
                                };
                            }

                            // NOTE - Audience City Chart
                            if (this.currentAccount.fbinsights.audience_city.length > 0) {
                                var tempData = [];
                                var tempLabels = [];
                                this.currentAccount.fbinsights.audience_city.forEach(
                                    (currentItem) => {
                                        tempData.push(currentItem.value);
                                        tempLabels.push(currentItem.insight_sub);
                                    }
                                );
                                this.currentAccount.fbinsights_charts.audienceCitySeries = [
                                    {
                                        name: "City",
                                        data: tempData,
                                    },
                                ];
                                this.currentAccount.fbinsights_charts.audienceCityOptions = {
                                    chart: {
                                        type: "bar",
                                        height: 350,
                                    },
                                    plotOptions: {
                                        bar: {
                                            borderRadius: 4,
                                            horizontal: true,
                                        },
                                    },
                                    title: {
                                        text: "Audience split by Main Cities",
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    labels: tempLabels,
                                };
                            }

                            // NOTE - Audience Gender CHart
                            if (this.currentAccount.fbinsights.audience_gender_age.length > 0) {
                                var tempDataU = [];
                                var tempDataM = [];
                                var tempDataF = [];
                                var tempLabels = [];
                                this.currentAccount.fbinsights.audience_gender_age.forEach(
                                    (currentItem) => {
                                        tempDataU.push(currentItem.U);
                                        tempDataF.push(currentItem.F);
                                        tempDataM.push(currentItem.M);
                                        tempLabels.push(currentItem.tranche);
                                    }
                                );
                                this.currentAccount.fbinsights_charts.audienceGenderSeries = [
                                    {
                                        name: "Unspecified",
                                        data: tempDataU,
                                    },
                                    {
                                        name: "Men",
                                        data: tempDataM,
                                    },
                                    {
                                        name: "Women",
                                        data: tempDataF,
                                    },
                                ];
                                this.currentAccount.fbinsights_charts.audienceGenderOptions = {
                                    chart: {
                                        type: "bar",
                                        height: 350,
                                        stacked: true,
                                        stackType: "100%",
                                    },
                                    title: {
                                        text: "Audience split by gender",
                                    },
                                    plotOptions: {
                                        bar: {
                                            borderRadius: 4,
                                            horizontal: true,
                                            stacked: true,
                                        },
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    labels: tempLabels,
                                    // tooltip: {
                                    //     y: {
                                    //         formatter: function (value, opts) {
                                    //             let percent =
                                    //                 opts.w.globals.seriesPercent[opts.seriesIndex][
                                    //                     opts.dataPointIndex
                                    //                 ];
                                    //             return percent.toFixed(0) + "%";
                                    //         },
                                    //     },
                                    // },
                                };
                            }

                            // NOTE - Audience Language
                            if (this.currentAccount.fbinsights.audience_locale.length > 0) {
                                var tempData = [];
                                var tempLabels = [];
                                if (this.currentAccount.fbinsights.audience_locale.length > 0) {
                                    this.currentAccount.fbinsights.audience_locale.forEach(
                                        (currentItem) => {
                                            tempData.push(currentItem.value);
                                            tempLabels.push(currentItem.language);
                                        }
                                    );
                                }
                                this.currentAccount.fbinsights_charts.audienceLocaleSeries =
                                    tempData;
                                this.currentAccount.fbinsights_charts.audienceLocaleOptions = {
                                    chart: {
                                        type: "donut",
                                    },
                                    labels: tempLabels,
                                    plotOptions: {
                                        pie: {
                                            donut: {
                                                labels: {
                                                    show: true,
                                                },
                                            },
                                        },
                                    },
                                    legend: {
                                        verticalAlign: "middle",
                                    },
                                };
                            }

                            // NOTE - Impressions
                            if (this.currentAccount.fbinsights.impressions.length > 0) {
                                var tempDataReach = [];
                                var tempDataImpressions = [];
                                var tempLabels = [];
                                this.currentAccount.fbinsights.impressions.forEach(
                                    (currentItem) => {
                                        tempDataImpressions.push(currentItem.value);
                                        tempLabels.push(currentItem.date);
                                    }
                                );
                                this.currentAccount.fbinsights.reach.forEach((currentItem) => {
                                    tempDataReach.push(currentItem.value);
                                    tempLabels.push(currentItem.date);
                                });
                                this.currentAccount.fbinsights_charts.reachImpressionsSeries = [
                                    {
                                        name: "Impressions",
                                        data: tempDataImpressions,
                                    },
                                    {
                                        name: "Reach",
                                        data: tempDataReach,
                                    },
                                ];
                                this.currentAccount.fbinsights_charts.reachImpressionsOptions = {
                                    chart: {
                                        type: "area",
                                        height: 350,
                                    },
                                    title: {
                                        text: "Reach and Impressions for last 30 days",
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    stroke: {
                                        curve: "smooth",
                                    },
                                    xaxis: {
                                        type: "date",
                                        categories: tempLabels,
                                    },
                                };
                            }

                            // NOTE - Impressions
                            if (this.currentAccount.fbinsights.monthly.length > 0) {
                                var tempDataReach = [];
                                var tempDataImpressions = [];
                                var tempLabels = [];
                                this.currentAccount.fbinsights.monthly.forEach((currentItem) => {
                                    tempDataImpressions.push(currentItem.impressions);
                                    tempDataReach.push(currentItem.reach);
                                    tempLabels.push(currentItem.date);
                                });
                                this.currentAccount.fbinsights_charts.reachImpressionsByMonthSeries =
                                    [
                                        {
                                            name: "Impressions",
                                            data: tempDataImpressions,
                                        },
                                        {
                                            name: "Reach",
                                            data: tempDataReach,
                                        },
                                    ];
                                this.currentAccount.fbinsights_charts.reachImpressionsByMonthOptions =
                                    {
                                        chart: {
                                            type: "area",
                                            height: 350,
                                        },
                                        title: {
                                            text: "Reach and Impressions by Month",
                                        },
                                        dataLabels: {
                                            enabled: false,
                                        },
                                        stroke: {
                                            curve: "smooth",
                                        },
                                        xaxis: {
                                            type: "datetime",
                                            labels: {
                                                format: "MMM y",
                                                // datetimeFormatter: {
                                                //     year: "yyyy",
                                                //     month: "MMM 'yy",
                                                //     day: "dd MMM",
                                                //     hour: "HH:mm",
                                                // },
                                            },
                                            categories: tempLabels,
                                        },
                                        yaxis: {
                                            labels: {
                                                formatter: (value) => {
                                                    return this.abbreviateNumber(value);
                                                },
                                            },
                                        },
                                        tooltip: {
                                            shared: true,
                                            x: {
                                                show: true,
                                                format: "MMMM y",
                                                formatter: undefined,
                                            },
                                        },
                                    };
                            }

                            // NOTE - Growth
                            // if (this.currentAccount.fbinsights.growth.length > 0) {
                            //     var tempDataFollowers = [];
                            //     var tempDataFollowing = [];
                            //     var tempLabels = [];
                            //     this.currentAccount.fbinsights.growth.forEach((currentItem) => {
                            //         tempDataFollowers.push(currentItem.followers);
                            //         tempDataFollowing.push(currentItem.followings);
                            //         tempLabels.push(currentItem.date);
                            //     });
                            //     this.currentAccount.fbinsights_charts.growthFollowerSeries = [
                            //         {
                            //             name: "Followers",
                            //             data: tempDataFollowers,
                            //         },
                            //         // {
                            //         //     name: "Reach",
                            //         //     data: tempDataReach,
                            //         // },
                            //     ];
                            //     this.currentAccount.fbinsights_charts.growthFollowerOptions = {
                            //         chart: {
                            //             type: "area",
                            //             height: 350,
                            //         },
                            //         title: {
                            //             text: "Follower Growth",
                            //         },
                            //         dataLabels: {
                            //             enabled: false,
                            //         },
                            //         stroke: {
                            //             curve: "smooth",
                            //         },
                            //         xaxis: {
                            //             type: "date",
                            //             categories: tempLabels,
                            //         },
                            //     };
                            // }
                        }
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Reset post loader
                    this.currentAccount.fbinsightsIsLoading = false;
                });
        },
        loadGrowth(accountid) {
            // Reset all
            this.currentAccount.growthIsLoading = true;

            // Perform the next axios request
            axios
                .get("accounts/dashboard/account/" + accountid + "/growth")
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "FAIL") {
                        // Do nothing
                    }
                    if (data.status == "OK") {
                        // Check what page we are on
                        // TODO - Handle case where we don't have insights for that user
                        this.currentAccount.growthinsights = data.data;

                        if (typeof this.currentAccount.growthinsights != "undefined") {
                            // NOTE - Audience Country Chart
                            var tempData = [];
                            var tempLabels = [];

                            // NOTE - Growth
                            if (this.currentAccount.growthinsights.growth.length > 0) {
                                var tempDataFollowers = [];
                                var tempDataFollowing = [];
                                var tempLabels = [];
                                this.currentAccount.growthinsights.growth.forEach((currentItem) => {
                                    tempDataFollowers.push(currentItem.followers);
                                    tempDataFollowing.push(currentItem.followings);
                                    tempLabels.push(currentItem.date);
                                });
                                this.currentAccount.growthinsights_charts.growthFollowerSeries = [
                                    {
                                        name: "Followers",
                                        data: tempDataFollowers,
                                    },
                                    // {
                                    //     name: "Reach",
                                    //     data: tempDataReach,
                                    // },
                                ];
                                this.currentAccount.growthinsights_charts.growthFollowerOptions = {
                                    chart: {
                                        type: "area",
                                        height: 350,
                                    },
                                    title: {
                                        text: "Follower Growth",
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    stroke: {
                                        curve: "smooth",
                                    },
                                    xaxis: {
                                        type: "date",
                                        categories: tempLabels,
                                    },
                                };
                            }
                        }
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Reset post loader
                    this.currentAccount.growthIsLoading = false;
                });
        },
        loadCampaign(accountid) {
            // Reset all
            this.currentAccount.campaignResultsIsLoading = true;

            // Perform the next axios request
            axios
                .get("accounts/dashboard/account/" + accountid + "/campaignresults")
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    if (data.status == "OK") {
                        // Check what page we are on
                        this.currentAccount.campaignResults = data.data;

                        // Check if we have results
                        if (data.data.globals.campaign_exists) {
                            // Get statistics for graphs
                            var statistics = data.data.performance.statistics;

                            // Define progress chart data
                            this.currentAccount.campaignResultsChartSeries = [
                                statistics.totaltop9hashtags,
                                statistics.count10to25,
                                statistics.count26to50,
                                statistics.count51to75,
                                statistics.count76to100,
                                statistics.totalunrankedhashtags,
                            ];
                            this.currentAccount.campaignResultsChartLabels = [
                                "Top 9",
                                "Top 10 to 25",
                                "Top 26 to 50",
                                "Top 51 to 75",
                                "Top 76 to 100",
                                "Unranked",
                            ];
                            this.currentAccount.campaignResultsChartOptions = {
                                labels: this.currentAccount.campaignResultsChartLabels,
                                colors: [
                                    "#1dc9b7",
                                    "#366cf3",
                                    "#5867dd",
                                    "#36a3f7",
                                    "#ffb822",
                                    "#fd397a",
                                ],
                                chart: {
                                    type: "donut",
                                    animations: {
                                        enabled: true,
                                        easing: "easeinout",
                                        speed: 800,
                                        animateGradually: {
                                            enabled: true,
                                            delay: 150,
                                        },
                                        dynamicAnimation: {
                                            enabled: true,
                                            speed: 350,
                                        },
                                    },
                                },
                                responsive: [
                                    {
                                        breakpoint: 480,
                                        options: {
                                            chart: {
                                                height: 300,
                                            },
                                            legend: {
                                                position: "bottom",
                                            },
                                        },
                                    },
                                ],
                                plotOptions: {
                                    pie: {
                                        donut: {
                                            labels: {
                                                show: true,
                                            },
                                        },
                                    },
                                },
                                legend: {
                                    verticalAlign: "middle",
                                },
                            };
                        }
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Reset post loader
                    this.currentAccount.campaignResultsIsLoading = false;
                });
        },
        /**
         * NOTE - Modal to start a campaign
         */
        openAddCampaignModal() {
            // Reset all
            this.modals.campaign.errors = [];
            this.modals.campaign.isLoading = false;

            // Open up
            this.$refs["add-campaign-modal"].show();
        },
        addCampaign() {
            // Reset
            this.modals.campaign.errors = [];
            this.modals.campaign.isLoading = true;

            // Perform post
            axios
                .post("campaigns/new", {
                    account: this.currentAccount.user.username,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Handle response
                    if (data.status == "OK") {
                        // Everything is OK, we should redirect to the page of the campaign now
                        this.loadAccount(data.data);

                        // Increase the counter for active campaigns
                        this.dashboard.globals.campaigns_current++;

                        // Re-load the dashboard
                        this.loadDashboard();

                        // Close modal
                        this.$refs["add-campaign-modal"].hide();
                    }

                    // Handle errors
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.modals.campaign.errors.push({
                                type: item.type,
                                message: item.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.modals.campaign.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Turn preloader off
                    this.modals.campaign.isLoading = false;
                });
        },
        /**
         * NOTE - MOdal to delete a campaign
         */
        openStopCampaignModal() {
            // Reset
            this.modals.deletecampaign.errors = [];
            this.modals.deletecampaign.isLoading = false;

            // Open up
            this.$refs["delete-campaign-modal"].show();
        },
        stopCampaign() {
            // Reset
            this.modals.deletecampaign.errors = [];
            this.modals.deletecampaign.isLoading = true;

            // Make pretty
            var accountid = this.currentAccount.user.userid;

            // Perform POST
            axios
                .post("campaigns/delete", {
                    accountid: accountid,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "OK") {
                        // Fix
                        var hackdata = {
                            accountid: accountid,
                        };

                        // Everything is OK, we should redirect to the page of the campaign now
                        this.loadAccount(hackdata);

                        // Re-load the dashboard
                        this.loadDashboard();

                        // Close modal
                        this.$refs["delete-campaign-modal"].hide();

                        // Go to Dashboard
                        this.openAccountSelectionSection();
                    }

                    // Add error message
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.modals.deletecampaign.errors.push({
                                type: item.type,
                                message: item.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.modals.deletecampaign.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Reset loaders
                    this.modals.deletecampaign.isLoading = false;
                });
        },
        /**
         * NOTE - Modal to delete an account
         */
        openStopAccountMonitoringModal() {
            // Reset
            this.modals.deleteaccount.errors = [];
            this.modals.deleteaccount.isLoading = false;

            // Open up
            this.$refs["delete-account-modal"].show();
        },
        stopAccountMonitoring() {
            // Reset
            this.modals.deleteaccount.errors = [];
            this.modals.deleteaccount.isLoading = true;

            // Make pretty
            var accountid = this.currentAccount.user.globals.is_monitoring_id;

            // Perform POST
            axios
                .get("accounts/delete/" + accountid)
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "OK") {
                        // Re-load the dashboard
                        this.loadDashboard();

                        // Close modal
                        this.$refs["delete-account-modal"].hide();

                        // Go to Dashboard
                        this.openAccountSelectionSection();
                    }

                    // Add error message
                    if (data.messages.length > 0) {
                        data.messages.forEach((item) => {
                            this.modals.deleteaccount.errors.push({
                                type: item.type,
                                message: item.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.modals.deleteaccount.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Reset loaders
                    this.modals.deleteaccount.isLoading = false;
                });
        },
        setInsightsPanel(panel) {
            this.currentInsightsPanel = panel;
        },

        morePostsToLoad() {
            if (this.currentAccount.postsCurrentPage < this.currentAccount.posts.data.last_page) {
                return true;
            }
            return false;
        },
        isCurrentTab(tab) {
            if (this.currentTab == tab) {
                return true;
            }
            return false;
        },
        isCurrentTabSvgIcon(tab) {
            var base = "svg-icon svg-icon-2x ml-n2 mr-1";
            if (this.currentTab == tab) {
                base = base + " svg-icon-white";
            } else {
                base = base + " svg-icon-primary";
            }
            return base;
        },
        switchTab(tab) {
            this.currentTab = tab;
        },

        getAllSelectedItems() {
            // Get variable to store items
            var hSelection = [];

            // Generate array of all selected hashtags by table
            // Get all hashtags from campaign posts
            this.currentAccount.posts.data.data.forEach((post) => {
                // Check if we have campaign data for this post
                if (post.campaign_data) {
                    // Do ranked hashtags
                    post.campaign_data.rank_data.forEach((item) => {
                        if (item.selected == true) {
                            hSelection.push(item.hashtag);
                        }
                    });

                    // Do ranked hashtags
                    post.campaign_data.unranked.forEach((item) => {
                        if (item.selected == true) {
                            hSelection.push(item.hashtag);
                        }
                    });
                }

                // Check if we have hashtag data for non campaign posts
                if (post.hashtag_data) {
                    post.hashtag_data.data.forEach((item) => {
                        if (item.selected == true) {
                            hSelection.push(item.hashtag);
                        }
                    });
                }
            });

            // This section handles the overall performance table
            this.currentAccount.campaignResults.performance.individual_hashtag_performance.forEach(
                (item) => {
                    if (item.selected == true) {
                        hSelection.push(item.hashtag);
                    }
                }
            );

            // Remove duplicates
            hSelection = [...new Set(hSelection)];

            // Return variable
            this.selectedHashtags = hSelection;
        },

        selectAll(variant) {
            this.loopThroughTableAndSet(variant, true);
            this.getAllSelectedItems();
        },

        deselectAll(variant) {
            this.loopThroughTableAndSet(variant, false);
            this.getAllSelectedItems();
        },

        loopThroughTableAndSet(variant, value) {
            if (variant == "summary") {
                if (
                    this.currentAccount.campaignResults.performance.individual_hashtag_performance
                        .length > 0
                ) {
                    this.currentAccount.campaignResults.performance.individual_hashtag_performance.forEach(
                        (part, index, theArray) => {
                            this.$set(
                                this.currentAccount.campaignResults.performance
                                    .individual_hashtag_performance[index],
                                "selected",
                                value
                            );
                        }
                    );
                }
            }
        },

        handleEmitMessages(messages) {
            if (messages.length > 0) {
                messages.forEach((currentItem) => {
                    this.errors.push({ type: currentItem.type, message: currentItem.message });
                });
            }
        },
        onRowClicked(item) {
            // Hack
            if (item.selected) {
                this.$set(item, "selected", false);
            } else {
                this.$set(item, "selected", true);
            }

            // Get selected items
            this.getAllSelectedItems();
        },
        printReport() {
            // Make pretty
            var accountid = this.currentAccount.user.userid;

            // Perform query
            axios
                .post("enterprise/exportreport", {
                    accountid: accountid,
                    accounttype: "accountDashboard",
                })
                .then((response) => {
                    // Do nothing
                })
                .catch((error) => {
                    // Do nothing
                })
                .finally(() => {
                    // Close preloader
                    window.print();
                });
        },
    },

    mounted() {
        // Set breadcrumb
        this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Accounts Dashboard", route: "accountdashboard" },
        ]);

        // Get dashboard
        this.loadDashboard();
    },

    computed: {
        ...mapGetters({
            // currentUser: "auth/currentUser",
            // isPremium: "auth/isPremium",
            isEnterprise: "auth/isEnterprise",
        }),
        selectedHashtagsHashed() {
            return "#" + this.selectedHashtags.join(" #");
        },
        canAddCampaigns() {
            if (typeof this.dashboard.globals != "undefined") {
                if (typeof this.dashboard.globals.campaigns_current != "undefined") {
                    if (
                        this.dashboard.globals.campaigns_current <
                        this.dashboard.globals.campaigns_max
                    ) {
                        return true;
                    }
                }
            }
            return false;
        },
    },
};
</script>
